import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, AreaChart, Area, CartesianGrid } from "recharts";
import IntegrationsPanel from "@/webapp/government/IntegrationsPanel";
import SecurityCenter from "@/webapp/security/SecurityCenter";
import { MapContainer, TileLayer, CircleMarker, Polyline, Tooltip as MapTooltip, useMap, ZoomControl } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import {
  LayoutDashboard, Route as RouteIcon, Building2, ShieldCheck, AlertTriangle, MapPin,
  Search, KeyRound, ScrollText, LogOut, Loader2, Lock, CheckCircle2, Clock, BadgeCheck,
  Truck, Radio, Gauge, Map as MapIcon, FileText, Bell, RefreshCw, Download, TrendingUp,
  Snowflake, Camera, Thermometer, Layers, ChevronDown, ChevronUp, CloudSun, Video, TriangleAlert, Waypoints,
  PanelLeftClose, PanelLeftOpen, Megaphone, Plus, Trash2, X, Activity, Radar, Send, Satellite, LayoutGrid,
} from "lucide-react";

const NAV = [
  { id: "overview", label: "Overview", icon: LayoutDashboard, mode: "live" },
  { id: "map", label: "Operations Map", icon: MapIcon, mode: "live" },
  { id: "owners", label: "Fleet Owners", icon: Activity, mode: "live" },
  { id: "fleet", label: "Live Fleet", icon: Truck, mode: "sandbox" },
  { id: "eld", label: "ELD Auto-Log", icon: Radio, mode: "live" },
  { id: "corridors", label: "Corridors", icon: RouteIcon, mode: "live" },
  { id: "facilities", label: "Facilities", icon: Building2, mode: "sandbox" },
  { id: "compliance", label: "Compliance", icon: ShieldCheck, mode: "live" },
  { id: "exceptions", label: "Exceptions", icon: AlertTriangle, mode: "live" },
  { id: "zones", label: "Impound Zones", icon: MapPin, mode: "live" },
  { id: "subject", label: "Subject Lookup", icon: Search, mode: "live" },
  { id: "breakglass", label: "Break-glass", icon: KeyRound, mode: "live" },
  { id: "reports", label: "Reports", icon: FileText, mode: "live" },
  { id: "audit", label: "Audit Log", icon: ScrollText, mode: "live" },
];

const DataTag = ({ mode, className = "" }) => {
  const sandbox = mode === "sandbox";
  return (
    <span
      data-testid={`data-tag-${sandbox ? "sandbox" : "live"}`}
      className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider ${sandbox ? "border-amber-300 bg-amber-50 text-amber-700" : "border-emerald-300 bg-emerald-50 text-emerald-700"} ${className}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${sandbox ? "bg-amber-500" : "bg-emerald-500 animate-pulse"}`} />
      {sandbox ? "Sandbox" : "Live"}
    </span>
  );
};

const CARD = "rounded-sm border border-slate-200 bg-white p-4";
const pressureColor = (p) => ({ high: "text-red-700 bg-red-50 border border-red-200", moderate: "text-amber-700 bg-amber-50 border border-amber-200", low: "text-emerald-700 bg-emerald-50 border border-emerald-200" }[p] || "text-slate-600 bg-slate-50 border border-slate-200");
const sevColor = (s) => ({ high: "text-red-700 bg-red-50 border border-red-200", medium: "text-amber-700 bg-amber-50 border border-amber-200", low: "text-slate-600 bg-slate-50 border border-slate-200" }[s] || "text-slate-600 bg-slate-50 border border-slate-200");

const Loading = () => <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#FF5722]" /></div>;
const Bar = ({ pct }) => (
  <div className="h-1.5 w-full overflow-hidden rounded-none bg-slate-100">
    <div className="h-full bg-[#FF5722]" style={{ width: `${Math.min(100, pct || 0)}%` }} />
  </div>
);

function Section({ title, subtitle, children }) {
  return (
    <div>
      <div className="border-b border-slate-200 pb-3">
        <h1 className="font-display text-2xl font-semibold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      <div className="mt-5">{children}</div>
    </div>
  );
}

const ANN_SEV = {
  info: { label: "Info", dot: "bg-slate-400", chip: "border-slate-300 bg-slate-50 text-slate-600" },
  advisory: { label: "Advisory", dot: "bg-blue-500", chip: "border-blue-300 bg-blue-50 text-blue-700" },
  warning: { label: "Warning", dot: "bg-amber-500", chip: "border-amber-300 bg-amber-50 text-amber-700" },
  critical: { label: "Critical", dot: "bg-red-500", chip: "border-red-300 bg-red-50 text-red-700" },
};
const TYPE_COLOR = { Closure: "#dc2626", Incident: "#f97316", Roadworks: "#f59e0b", Hazard: "#7c3aed", Other: "#94a3b8" };
function timeAgo(iso) {
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60); if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function BreakdownBars({ items, colorFor, testid }) {
  if (!items || items.length === 0) return <p className="py-6 text-center font-mono text-[11px] text-slate-400">No live disruptions right now.</p>;
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <div className="space-y-2" data-testid={testid}>
      {items.map((i) => (
        <div key={i.label} className="flex items-center gap-2.5" data-testid={`${testid}-row`}>
          <span className="w-24 flex-shrink-0 truncate font-mono text-[10px] font-semibold uppercase tracking-wider text-slate-500">{i.label}</span>
          <div className="h-2.5 flex-1 overflow-hidden rounded-sm bg-slate-100">
            <div className="h-full rounded-sm transition-[width] duration-500" style={{ width: `${Math.max(3, (i.count / max) * 100)}%`, backgroundColor: colorFor ? colorFor(i.label) : "#FF5722" }} />
          </div>
          <span className="w-9 flex-shrink-0 text-right font-mono text-xs font-semibold tabular-nums text-slate-900">{i.count}</span>
        </div>
      ))}
    </div>
  );
}

function Overview() {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [impound, setImpound] = useState(null);
  const [cands, setCands] = useState([]);
  const [zoneDrill, setZoneDrill] = useState(null);
  useEffect(() => {
    api.get("/gov/overview").then((r) => setData(r.data)).catch(() => setData({ kpis: {}, corridors: [] }));
    api.get("/gov/alerts").then((r) => setAlerts(r.data.alerts || [])).catch(() => setAlerts([]));
    api.get("/gov/impound/summary").then((r) => setImpound(r.data)).catch(() => setImpound({ zones: [], total_held: 0, recent: [] }));
    api.get("/gov/impound/candidates").then((r) => setCands(r.data.candidates || [])).catch(() => setCands([]));
  }, []);
  if (!data) return <Loading />;
  const k = data.kpis || {};
  const kpis = [
    { label: "Corridors Monitored", value: k.corridors_monitored },
    { label: "Facilities", value: k.facilities },
    { label: "Live Disruptions", value: k.live_disruptions ?? 0, hot: (k.live_closures ?? 0) > 0, sub: (k.live_closures ?? 0) > 0 ? `${k.live_closures} closure${k.live_closures > 1 ? "s" : ""}` : "live now" },
    { label: "Incidents (30d)", value: k.incidents_30d },
    { label: "Open Exceptions", value: k.open_exceptions },
    { label: "Active Impound Zones", value: k.active_impound_zones },
  ];
  const disruptions = data.disruptions || [];
  const byType = data.breakdown?.by_type || [];
  const byState = data.breakdown?.by_state || [];
  return (
    <Section title="Corridor Overview" subtitle={`Jurisdiction: ${data.jurisdiction || "Alberta, Canada"} — public-benefit safety & utilisation.`}>
      {disruptions.length > 0 && (
        <div data-testid="gov-disruptions-ribbon" className="mb-4 flex items-stretch overflow-hidden rounded-sm border border-red-300">
          <span className="flex flex-shrink-0 items-center gap-1.5 bg-red-600 px-3 py-2 font-mono text-[11px] font-bold uppercase tracking-widest text-white"><TriangleAlert className="h-3.5 w-3.5" /> Live · Biggest disruptions</span>
          <div className="flex flex-1 items-center gap-2 overflow-x-auto whitespace-nowrap bg-red-50 px-3 py-2">
            {disruptions.map((x) => (
              <span key={x.id} data-testid="gov-disruption-item" className={`flex-shrink-0 rounded-sm px-2.5 py-1 font-mono text-[11px] font-semibold uppercase tracking-wide ${x.full_closure ? "bg-red-600 text-white" : "border border-amber-300 bg-amber-100 text-amber-800"}`}>
                {x.full_closure ? "▲ CLOSED · " : "• "}{x.road ? `${x.road} — ` : ""}{(x.headline || "").slice(0, 56)}{x.state ? ` · ${x.state}` : ""}
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-sm border border-slate-200 bg-slate-200 sm:grid-cols-3 lg:grid-cols-6">
        {kpis.map((x) => (
          <div key={x.label} data-testid={`gov-kpi-${x.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={`bg-white p-3 ${x.hot ? "bg-red-50" : ""}`}>
            <p className="font-mono text-[10px] font-semibold uppercase tracking-wider text-slate-400">{x.label}</p>
            <p className={`mt-1.5 font-mono text-3xl font-medium tracking-tight ${x.hot ? "text-red-600" : "text-slate-900"}`}>{x.value ?? "—"}</p>
            {x.sub ? <p className="mt-0.5 font-mono text-[10px] text-slate-400">{x.sub}</p> : null}
          </div>
        ))}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <div className="grid gap-4 sm:grid-cols-2 lg:col-span-2">
          <div className={CARD} data-testid="gov-breakdown-type">
            <div className="mb-3 flex items-center justify-between">
              <p className="flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-700"><Activity className="h-3.5 w-3.5 text-[#FF5722]" /> Disruptions · by type</p>
              <DataTag mode="live" />
            </div>
            <BreakdownBars items={byType} colorFor={(l) => TYPE_COLOR[l] || "#94a3b8"} testid="breakdown-type-bars" />
          </div>
          <div className={CARD} data-testid="gov-breakdown-state">
            <div className="mb-3 flex items-center justify-between">
              <p className="flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-700"><Radar className="h-3.5 w-3.5 text-[#FF5722]" /> Disruptions · by region</p>
              <DataTag mode="live" />
            </div>
            <BreakdownBars items={byState} testid="breakdown-state-bars" />
          </div>
        </div>

        <AnnouncementsPanel />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <div className={`${CARD} lg:col-span-2`}>
          <p className="mb-4 flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-700"><Gauge className="h-3.5 w-3.5 text-[#FF5722]" /> Corridor safety pressure</p>
          <CorridorRows corridors={data.corridors} />
        </div>
        <div className={CARD}>
          <p className="mb-3 flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-700"><Bell className="h-3.5 w-3.5 text-[#FF5722]" /> Priority alerts</p>
          <div className="max-h-[260px] space-y-2 overflow-y-auto">
            {alerts.length === 0 && <p className="py-4 text-center font-mono text-[11px] text-slate-400">No active alerts.</p>}
            {alerts.slice(0, 10).map((a) => (
              <div key={a.id} data-testid="gov-alert-row" className="flex items-start gap-2 rounded-sm border border-slate-100 bg-slate-50 px-3 py-2">
                <span className={`mt-1 h-2 w-2 flex-shrink-0 rounded-full ${a.severity === "high" ? "bg-red-500" : a.severity === "medium" ? "bg-amber-500" : "bg-slate-400"}`} />
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-900">{a.kind} <span className="font-normal text-slate-400">· {a.corridor}</span></p>
                  <p className="truncate text-xs text-slate-500">{a.summary}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <div className={CARD}>
          <div className="mb-3 flex items-center justify-between">
            <p className="flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-700"><MapPin className="h-3.5 w-3.5 text-[#FF5722]" /> Active impound zones</p>
            <span className="rounded-sm bg-slate-100 px-1.5 font-mono text-[10px] tabular-nums text-slate-600">{impound?.zones?.length ?? 0}</span>
          </div>
          <div className="max-h-[220px] space-y-2 overflow-y-auto">
            {(!impound || impound.zones.length === 0) && <p className="py-6 text-center font-mono text-[11px] text-slate-400">No active impound zones.</p>}
            {impound?.zones?.map((z) => (
              <button key={z.zone_id} type="button" onClick={() => setZoneDrill(z)} data-testid="gov-impound-zone-row" className="flex w-full items-center justify-between gap-2 rounded-sm border border-slate-100 bg-slate-50 px-3 py-2 text-left transition-colors hover:border-[#FF5722]/40 hover:bg-white">
                <div className="min-w-0"><p className="truncate text-sm font-semibold text-slate-900">{z.name}</p><p className="truncate font-mono text-[10px] text-slate-400">{z.location || "—"}  ▸ drilldown</p></div>
                <span className="flex-shrink-0 rounded-sm bg-amber-100 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-amber-700">{z.held} held</span>
              </button>
            ))}
          </div>
        </div>
        <div className={CARD}>
          <div className="mb-3 flex items-center justify-between">
            <p className="flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-700"><TriangleAlert className="h-3.5 w-3.5 text-[#FF5722]" /> Impound candidates</p>
            <span className="rounded-sm bg-red-100 px-1.5 font-mono text-[10px] font-bold tabular-nums text-red-700">{cands.length}</span>
          </div>
          <div className="max-h-[220px] space-y-2 overflow-y-auto">
            {cands.length === 0 && <p className="py-6 text-center font-mono text-[11px] text-slate-400">No vehicles flagged for impound.</p>}
            {cands.slice(0, 12).map((v, i) => (
              <div key={i} data-testid="gov-impound-candidate-row" className="flex items-start gap-2 rounded-sm border border-slate-100 bg-slate-50 px-3 py-2">
                <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-red-500" />
                <div className="min-w-0"><p className="truncate text-sm font-semibold text-slate-900">{v.vehicle_ref || v.vehicle || "Vehicle"}</p><p className="truncate text-xs text-slate-500">{v.reason || v.headline || "Flagged"}{v.suggested_zone ? ` · → ${v.suggested_zone}` : ""}</p></div>
              </div>
            ))}
          </div>
        </div>
        <div className={CARD}>
          <p className="mb-3 flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-700"><ScrollText className="h-3.5 w-3.5 text-[#FF5722]" /> Recent impound actions</p>
          <div className="max-h-[220px] space-y-2 overflow-y-auto">
            {(!impound || impound.recent.length === 0) && <p className="py-6 text-center font-mono text-[11px] text-slate-400">No impound actions yet.</p>}
            {impound?.recent?.map((r, i) => (
              <div key={i} data-testid="gov-impound-recent-row" className="rounded-sm border border-slate-100 bg-slate-50 px-3 py-2">
                <p className="text-xs font-semibold text-slate-900"><span className={r.action === "impound.tow_in" ? "text-red-600" : "text-emerald-600"}>{r.action === "impound.tow_in" ? "TOW-IN" : "RELEASE"}</span> · {r.resource_id}</p>
                <p className="truncate font-mono text-[10px] text-slate-400">{timeAgo(r.ts)} ago · {r.actor_email}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {zoneDrill && <ImpoundZoneModal zone={zoneDrill} onClose={() => setZoneDrill(null)} />}
    </Section>
  );
}

function ImpoundZoneModal({ zone, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/gov/impound/zone/${zone.zone_id}`).then((r) => setData(r.data)).catch(() => setData({ zone, held: [] }));
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zone, onClose]);
  const held = data?.held || [];
  const hasGeo = zone.lat != null && zone.lng != null;
  return (
    <div data-testid="gov-impound-drilldown" onClick={onClose} className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm">
      <div onClick={(e) => e.stopPropagation()} className="flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-sm border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-amber-50 px-4 py-2.5">
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 truncate text-sm font-bold text-slate-900"><MapPin className="h-3.5 w-3.5 text-amber-600" /> {zone.name}</p>
            <p className="truncate font-mono text-[11px] text-slate-500">{zone.location || "Impound zone"}</p>
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            <span className="rounded-sm bg-amber-200 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-amber-800">{held.length} held</span>
            <button data-testid="gov-impound-drilldown-close" onClick={onClose} className="flex h-7 w-7 items-center justify-center rounded-sm border border-slate-200 text-slate-500 transition-colors hover:border-red-300 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
          </div>
        </div>
        {hasGeo && (
          <div className="h-52 flex-shrink-0 border-b border-slate-200">
            <MapContainer center={[zone.lat, zone.lng]} zoom={12} zoomControl={false} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
              <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}" />
              <CircleMarker center={[zone.lat, zone.lng]} radius={16} pathOptions={{ color: "#d97706", weight: 3, fillColor: "#f59e0b", fillOpacity: 0.25 }} />
              <CircleMarker center={[zone.lat, zone.lng]} radius={5} pathOptions={{ color: "#b45309", weight: 2, fillColor: "#d97706", fillOpacity: 1 }} />
            </MapContainer>
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          <p className="mb-2 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">Vehicles currently held</p>
          {data === null && <p className="py-6 text-center font-mono text-[11px] text-slate-400">Loading…</p>}
          {data && held.length === 0 && <p className="py-6 text-center font-mono text-[11px] text-slate-400">No vehicles currently held in this zone.</p>}
          <div className="space-y-1.5">
            {held.map((h) => (
              <div key={h.entry_id} data-testid="gov-impound-held-row" className="flex items-start justify-between gap-2 rounded-sm border border-slate-100 bg-slate-50 px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-900">{h.vehicle_ref}</p>
                  <p className="truncate text-xs text-slate-500">{h.reason || "Impounded"}{h.lawful_basis ? ` · basis: ${h.lawful_basis}` : ""}</p>
                </div>
                <span className="flex-shrink-0 font-mono text-[10px] text-slate-400">{h.created_at ? `${timeAgo(h.created_at)} ago` : ""}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function AnnouncementsPanel() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", severity: "info" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = useCallback(() => { api.get("/gov/announcements").then((r) => setItems(r.data.announcements || [])).catch(() => setItems([])); }, []);
  useEffect(() => { load(); }, [load]);
  const publish = async () => {
    setErr("");
    if (!form.title.trim() || !form.body.trim()) { setErr("Title and message are required."); return; }
    setBusy(true);
    try { await api.post("/gov/announcements", form); setForm({ title: "", body: "", severity: "info" }); setOpen(false); load(); }
    catch (e) { setErr(formatApiError(e)); }
    setBusy(false);
  };
  const remove = async (id) => { try { await api.delete(`/gov/announcements/${id}`); load(); } catch {} };
  return (
    <div className={CARD} data-testid="gov-announcements">
      <div className="mb-3 flex items-center justify-between">
        <p className="flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-700"><Megaphone className="h-3.5 w-3.5 text-[#FF5722]" /> Announcements</p>
        <button data-testid="gov-announce-toggle" onClick={() => { setOpen((v) => !v); setErr(""); }} className="flex items-center gap-1 rounded-sm border border-slate-300 px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-slate-600 transition-colors hover:border-[#FF5722] hover:text-[#FF5722]">
          {open ? <><X className="h-3 w-3" /> Cancel</> : <><Plus className="h-3 w-3" /> New</>}
        </button>
      </div>

      {open && (
        <div className="mb-3 space-y-2 rounded-sm border border-slate-200 bg-slate-50 p-3" data-testid="gov-announce-form">
          <Input data-testid="gov-announce-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Announcement title" className="h-8 rounded-sm border-slate-300 bg-white text-sm" />
          <textarea data-testid="gov-announce-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Official message to display on the console…" rows={3} className="w-full resize-none rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#FF5722]" />
          <div className="flex items-center gap-1.5">
            {Object.entries(ANN_SEV).map(([key, s]) => (
              <button key={key} data-testid={`gov-announce-sev-${key}`} onClick={() => setForm({ ...form, severity: key })} className={`rounded-sm border px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-wider transition-colors ${form.severity === key ? s.chip : "border-slate-200 bg-white text-slate-400 hover:text-slate-600"}`}>
                {s.label}
              </button>
            ))}
          </div>
          {err && <p className="font-mono text-[11px] text-red-600" data-testid="gov-announce-error">{err}</p>}
          <Button data-testid="gov-announce-publish" onClick={publish} disabled={busy} className="h-8 w-full rounded-sm bg-[#FF5722] text-xs font-semibold uppercase tracking-wider hover:bg-[#e64a19]">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="mr-1.5 h-3.5 w-3.5" /> Publish</>}
          </Button>
        </div>
      )}

      <div className="max-h-[260px] space-y-2 overflow-y-auto">
        {items === null && <p className="py-4 text-center font-mono text-[11px] text-slate-400">Loading…</p>}
        {items && items.length === 0 && <p className="py-6 text-center font-mono text-[11px] text-slate-400">No announcements published.</p>}
        {items && items.map((a) => {
          const s = ANN_SEV[a.severity] || ANN_SEV.info;
          return (
            <div key={a.id} data-testid="gov-announcement-row" className="group rounded-sm border border-slate-100 bg-slate-50 px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <span className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider ${s.chip}`}><span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} /> {s.label}</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] text-slate-400">{timeAgo(a.created_at)} ago</span>
                  {a.can_delete && <button data-testid="gov-announce-delete" onClick={() => remove(a.id)} className="text-slate-300 opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100"><Trash2 className="h-3.5 w-3.5" /></button>}
                </div>
              </div>
              <p className="mt-1 text-sm font-semibold text-slate-900">{a.title}</p>
              <p className="mt-0.5 whitespace-pre-wrap text-xs text-slate-600">{a.body}</p>
              <p className="mt-1 font-mono text-[10px] text-slate-400">— {a.author_name || a.author_email}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const HOS_COLOR = { driving: "#10b981", on_duty: "#3b82f6", sleeper: "#8b5cf6", off_duty: "#94a3b8", unknown: "#94a3b8" };
const BAND_COLOR = { low: "#10b981", moderate: "#f59e0b", high: "#f97316", severe: "#ef4444" };

function decodePolyline(str, precision = 5) {
  if (!str) return [];
  let index = 0, lat = 0, lng = 0;
  const coords = [], factor = Math.pow(10, precision);
  while (index < str.length) {
    let result = 1, shift = 0, b;
    do { b = str.charCodeAt(index++) - 63 - 1; result += b << shift; shift += 5; } while (b >= 0x1f);
    lat += result & 1 ? ~(result >> 1) : result >> 1;
    result = 1; shift = 0;
    do { b = str.charCodeAt(index++) - 63 - 1; result += b << shift; shift += 5; } while (b >= 0x1f);
    lng += result & 1 ? ~(result >> 1) : result >> 1;
    coords.push([lat / factor, lng / factor]);
  }
  return coords;
}

function ClusterLayer({ items, radius = 5, stroke, fill, tooltip, popupHtml, enabled = true }) {
  const map = useMap();
  useEffect(() => {
    if (!enabled || !items || !items.length) return undefined;
    const group = L.markerClusterGroup({ chunkedLoading: true, maxClusterRadius: 55, disableClusteringAtZoom: 11, showCoverageOnHover: false });
    items.forEach((it) => {
      if (it.lat == null || it.lng == null) return;
      const m = L.circleMarker([it.lat, it.lng], { radius, color: stroke, weight: 1, fillColor: fill, fillOpacity: 0.9 });
      if (popupHtml) m.bindPopup(popupHtml(it), { maxWidth: 260 });
      else if (tooltip) m.bindTooltip(tooltip(it));
      group.addLayer(m);
    });
    map.addLayer(group);
    return () => { map.removeLayer(group); };
  }, [map, items, enabled, radius, stroke, fill, tooltip, popupHtml]);
  return null;
}

function WinterRoadsLayer({ segments, enabled = true }) {
  const map = useMap();
  useEffect(() => {
    if (!enabled || !segments || !segments.length) return undefined;
    const group = L.layerGroup();
    segments.forEach((s) => {
      const pts = decodePolyline(s.polyline);
      if (pts.length < 2) return;
      const pl = L.polyline(pts, { color: s.color || "#94a3b8", weight: 4, opacity: 0.8 });
      pl.bindTooltip(`${s.road || s.location} — ${s.condition || "condition n/a"}${s.visibility ? ` · vis ${s.visibility}` : ""}`);
      group.addLayer(pl);
    });
    map.addLayer(group);
    return () => { map.removeLayer(group); };
  }, [map, segments, enabled]);
  return null;
}

function AuRoadNetworkLayer({ ways, enabled = true }) {
  const map = useMap();
  useEffect(() => {
    if (!enabled || !ways || !ways.length) return undefined;
    const renderer = L.canvas({ padding: 0.5 });
    const mot = [], trunk = [];
    for (const w of ways) { if (w.g && w.g.length >= 2) (w.c === "m" ? mot : trunk).push(w.g); }
    const group = L.layerGroup();
    if (trunk.length) {
      group.addLayer(L.polyline(trunk, { renderer, color: "#f59e0b", weight: 3, opacity: 0.18, interactive: false }));
      group.addLayer(L.polyline(trunk, { renderer, color: "#d97706", weight: 1.3, opacity: 0.9, interactive: false }));
    }
    if (mot.length) {
      group.addLayer(L.polyline(mot, { renderer, color: "#10b981", weight: 4, opacity: 0.2, interactive: false }));
      group.addLayer(L.polyline(mot, { renderer, color: "#047857", weight: 1.8, opacity: 0.95, interactive: false }));
    }
    map.addLayer(group);
    return () => { map.removeLayer(group); };
  }, [map, ways, enabled]);
  return null;
}

function AuHazardOverlayLayer({ red, amber, enabled = true }) {
  const map = useMap();
  useEffect(() => {
    if (!enabled || (!red?.length && !amber?.length)) return undefined;
    const renderer = L.canvas({ padding: 0.5 });
    const group = L.layerGroup();
    if (amber?.length) {
      group.addLayer(L.polyline(amber, { renderer, color: "#f59e0b", weight: 6, opacity: 0.35, interactive: false }));
      group.addLayer(L.polyline(amber, { renderer, color: "#fbbf24", weight: 2.4, opacity: 0.95, interactive: false }));
    }
    if (red?.length) {
      group.addLayer(L.polyline(red, { renderer, color: "#ef4444", weight: 8, opacity: 0.45, interactive: false }));
      group.addLayer(L.polyline(red, { renderer, color: "#f87171", weight: 2.8, opacity: 1, interactive: false }));
    }
    map.addLayer(group);
    return () => { map.removeLayer(group); };
  }, [map, red, amber, enabled]);
  return null;
}

function computeHazardWays(ways, events) {
  if (!ways?.length || !events?.length) return { red: [], amber: [] };
  const CELL = 20;
  const key = (lat, lng) => `${Math.round(lat * CELL)},${Math.round(lng * CELL)}`;
  const grid = new Map();
  ways.forEach((w, i) => {
    const seen = new Set();
    for (const p of w.g) { const c = key(p[0], p[1]); if (!seen.has(c)) { seen.add(c); let a = grid.get(c); if (!a) grid.set(c, (a = [])); a.push(i); } }
  });
  const redIdx = new Set(), amberIdx = new Set();
  for (const e of events) {
    if (e.lat == null || e.lng == null) continue;
    const isRed = e.full_closure || e.severity === "high";
    const el = Math.round(e.lat * CELL), en = Math.round(e.lng * CELL);
    for (let a = -1; a <= 1; a++) for (let b = -1; b <= 1; b++) {
      const arr = grid.get(`${el + a},${en + b}`); if (!arr) continue;
      for (const wi of arr) (isRed ? redIdx : amberIdx).add(wi);
    }
  }
  for (const wi of redIdx) amberIdx.delete(wi);
  return { red: [...redIdx].map((i) => ways[i].g), amber: [...amberIdx].map((i) => ways[i].g) };
}

const LAYER_TOGGLES = [
  { key: "network", label: "Freight network", color: "#34d399", icon: Waypoints },
  { key: "winter", label: "Road conditions", color: "#10b981", icon: Snowflake },
  { key: "events", label: "Road events", color: "#dc2626", icon: TriangleAlert },
  { key: "cameras", label: "CCTV cameras", color: "#0ea5e9", icon: Video },
  { key: "rwis", label: "RWIS stations", color: "#14b8a6", icon: Thermometer },
];

function FlyController({ target }) {
  const map = useMap();
  const [r, setR] = useState(12);
  useEffect(() => {
    if (!target || target.lat == null) return undefined;
    map.flyTo([target.lat, target.lng], Math.max(map.getZoom(), 10), { duration: 1.1 });
    const start = Date.now(); let raf;
    const tick = () => { const t = (Date.now() - start) / 1000; setR(12 + 9 * Math.abs(Math.sin(t * 3))); if (t < 4) raf = requestAnimationFrame(tick); else setR(12); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, map]);
  if (!target || target.lat == null) return null;
  return <CircleMarker center={[target.lat, target.lng]} radius={r} pathOptions={{ color: "#dc2626", weight: 3, fillColor: "#dc2626", fillOpacity: 0.15 }} />;
}

function CommandMap() {
  const [d, setD] = useState(null);
  const [cands, setCands] = useState([]);
  const [roadNet, setRoadNet] = useState([]);
  const [flyTarget, setFlyTarget] = useState(null);
  const [layers, setLayers] = useState({ network: true, winter: true, events: true, cameras: true, rwis: true });
  const [legendOpen, setLegendOpen] = useState(false);
  const [basemap, setBasemap] = useState("map");
  const [refreshMs, setRefreshMs] = useState(60000);
  const [updated, setUpdated] = useState(null);
  const [clock, setClock] = useState("");
  const [layerQuery, setLayerQuery] = useState("");
  const [closuresOpen, setClosuresOpen] = useState(true);
  const [layersOpen, setLayersOpen] = useState(true);
  const toggle = (k) => setLayers((p) => ({ ...p, [k]: !p[k] }));
  const reload = useCallback(() => {
    api.get(`/gov/map`).then((r) => { setD(r.data); setUpdated(new Date()); }).catch(() => {});
    api.get(`/gov/impound/candidates`).then((r) => setCands(r.data.candidates || [])).catch(() => setCands([]));
  }, []);
  useEffect(() => {
    api.get(`/gov/map`).then((r) => {
      setD(r.data); setUpdated(new Date());
      if ((r.data.region || "ab") === "au") api.get(`/gov/roads-network`).then((rn) => setRoadNet(rn.data.ways || [])).catch(() => setRoadNet([]));
    }).catch(() => setD({ region: "ab", vehicles: [], zones: [], facilities: [], corridors: [], road_events: [], cameras: [], weather_stations: [], winter_roads: [], alerts: [], emergencies: [], source: "not connected", center: [53, -115.5] }));
    api.get(`/gov/impound/candidates`).then((r) => setCands(r.data.candidates || [])).catch(() => setCands([]));
  }, []);
  useEffect(() => { const f = () => setClock(new Date().toISOString().slice(11, 19)); f(); const t = setInterval(f, 1000); return () => clearInterval(t); }, []);
  useEffect(() => { if (!refreshMs) return undefined; const t = setInterval(reload, refreshMs); return () => clearInterval(t); }, [refreshMs, reload]);
  const hazardWays = useMemo(() => computeHazardWays(roadNet, d?.road_events || []), [roadNet, d]);

  if (!d) return <Loading />;
  const region = d.region || "ab";
  const center = d.center || [53.0, -115.5];
  const liveVeh = (d.source || "").includes("samsara") || (d.source || "").includes("geotab");
  const isAb = region === "ab";
  const jurLabel = isAb ? "🇨🇦 Alberta, Canada" : "🇦🇺 Australia";
  const closures = (d.road_events || []).filter((e) => e.full_closure).length;
  const CLAY = "border border-slate-300 shadow-lg";
  const CLAY_SM = "border border-slate-200";
  const CLAY_IN = "border border-slate-300 bg-slate-100";

  const sources = isAb
    ? [
        { id: "weather", label: "Weather", detail: d.weather_source, ok: true, testid: null },
        { id: "roads", label: "Roads", detail: d.roads_source, ok: d.roads_source && d.roads_source !== "unavailable", testid: "gov-map-roads-source" },
        { id: "cameras", label: "Cameras", detail: d.cameras_source, ok: (d.cameras || []).length > 0, testid: "gov-map-cameras-source" },
        { id: "rwis", label: "RWIS", detail: d.rwis_source, ok: (d.weather_stations || []).length > 0, testid: "gov-map-rwis-source" },
        { id: "winter", label: "Conditions", detail: d.winter_source, ok: (d.winter_roads || []).length > 0, testid: "gov-map-winter-source" },
        { id: "fleet", label: "Fleet", detail: liveVeh ? d.source : "not connected", ok: liveVeh, testid: "gov-map-fleet-source" },
      ]
    : [
        { id: "cameras", label: "Cameras", detail: d.cameras_source, ok: (d.cameras || []).length > 0, testid: "gov-map-cams-source" },
        { id: "rwis", label: "Road-weather", detail: d.rwis_source, ok: (d.weather_stations || []).length > 0, testid: "gov-map-rwis-source" },
        { id: "roads", label: "Roads", detail: d.roads_source, ok: (d.road_events || []).length > 0, testid: "gov-map-roads-source" },
        { id: "emerg", label: "Emergencies", detail: d.emergency_source, ok: (d.emergencies || []).length > 0, testid: "gov-map-emerg-source" },
        { id: "fleet", label: "Fleet", detail: liveVeh ? d.source : "not connected", ok: liveVeh, testid: "gov-map-fleet-source" },
      ];

  const kpis = isAb
    ? [{ n: (d.cameras || []).length, l: "Cameras" }, { n: (d.weather_stations || []).length, l: "Stations" }, { n: (d.road_events || []).length, l: "Events" }, { n: closures, l: "Closures", hot: closures > 0 }]
    : [{ n: (d.cameras || []).length, l: "Cameras" }, { n: (d.weather_stations || []).length, l: "Stations" }, { n: (d.road_events || []).length, l: "Road events" }, { n: closures, l: "Closures", hot: closures > 0 }];

  const legendItems = [
    { t: "line", c: "#10b981", label: "Corridor risk low" },
    { t: "line", c: "#f97316", label: "Corridor high" },
    { t: "line", c: "#ef4444", label: "Corridor severe" },
    { t: "line", c: "#10b981", label: "Road condition" },
    { t: "dot", c: "#dc2626", label: "Road event" },
    { t: "ring", c: "#dc2626", label: "Full closure" },
    { t: "dot", c: "#0ea5e9", label: "CCTV camera" },
    { t: "dot", c: "#14b8a6", label: "RWIS station" },
    { t: "dot", c: "#ef4444", label: "Impound zone" },
    { t: "dot", c: "#FF5722", label: "Flagged vehicle" },
    { t: "line", c: "#FF5722", label: "Tow route" },
    { t: "dot", c: "#0A234A", label: "Facility" },
    ...(isAb ? [] : [{ t: "line", c: "#047857", label: "Motorway network" }, { t: "line", c: "#d97706", label: "Highway (trunk)" }, { t: "line", c: "#f87171", label: "Network near closure" }, { t: "line", c: "#f59e0b", label: "Network near roadwork" }, { t: "dot", c: "#7c3aed", label: "Emergency" }]),
  ];

  const layerCount = { network: roadNet.length, winter: (d.winter_roads || []).length, events: (d.road_events || []).length, cameras: (d.cameras || []).length, rwis: (d.weather_stations || []).length };
  const idx = closures >= 5
    ? { label: `High · ${closures} closures`, chip: "border-red-300 bg-red-50 text-red-700", dot: "bg-red-500" }
    : closures > 0
    ? { label: `Elevated · ${closures} closure${closures > 1 ? "s" : ""}`, chip: "border-amber-300 bg-amber-50 text-amber-700", dot: "bg-amber-500" }
    : { label: "Nominal", chip: "border-emerald-300 bg-emerald-50 text-emerald-700", dot: "bg-emerald-500" };
  const REFRESH = [["30s", 30000], ["1m", 60000], ["5m", 300000], ["Off", 0]];
  const visibleLayers = LAYER_TOGGLES.filter((lt) => (lt.key === "network" ? !isAb : lt.key === "winter" ? isAb : true)).filter((lt) => lt.label.toLowerCase().includes(layerQuery.toLowerCase()));

  return (
    <div data-testid="gov-command-center" className="-mx-5 -my-6 flex h-[calc(100dvh-56px)] flex-col bg-slate-100 lg:-mx-8">
      <div className="flex h-12 flex-shrink-0 items-center gap-2.5 border-b border-slate-200 bg-white px-4">
        <span className="flex items-center gap-1.5 font-mono text-[12px] font-bold uppercase tracking-widest text-slate-900"><Radar className="h-4 w-4 text-[#FF5722]" /> FleetPods <span className="hidden text-slate-400 sm:inline">· Operations</span></span>
        <span className="hidden h-5 w-px bg-slate-200 sm:block" />
        <span data-testid="cc-jurisdiction" className="hidden items-center gap-1.5 rounded-sm border border-slate-200 bg-slate-50 px-2.5 py-1 font-mono text-[11px] font-semibold text-slate-600 sm:flex">{jurLabel}</span>
        <span data-testid="cc-disruption-index" className={`flex items-center gap-1.5 rounded-sm border px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider ${idx.chip}`}><span className={`h-1.5 w-1.5 rounded-full ${idx.dot}`} /> Disruptions · {idx.label}</span>
        <div className="ml-auto flex items-center gap-2">
          <div className="hidden items-center overflow-hidden rounded-sm border border-slate-200 md:flex">
            {REFRESH.map(([lbl, ms]) => (
              <button key={lbl} data-testid={`cc-refresh-${lbl}`} onClick={() => setRefreshMs(ms)} className={`px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wider transition-colors ${refreshMs === ms ? "bg-[#FF5722] text-white" : "bg-white text-slate-500 hover:text-slate-800"}`}>{lbl}</button>
            ))}
          </div>
          <button data-testid="cc-refresh-now" onClick={reload} title="Refresh now" className="flex h-7 w-7 items-center justify-center rounded-sm border border-slate-200 text-slate-500 transition-colors hover:border-[#FF5722] hover:text-[#FF5722]"><RefreshCw className="h-3.5 w-3.5" /></button>
          <div className="hidden items-center overflow-hidden rounded-sm border border-slate-200 md:flex">
            <button data-testid="cc-basemap-map" onClick={() => setBasemap("map")} className={`flex items-center gap-1 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wider transition-colors ${basemap === "map" ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:text-slate-800"}`}><MapIcon className="h-3 w-3" /> Map</button>
            <button data-testid="cc-basemap-sat" onClick={() => setBasemap("sat")} className={`flex items-center gap-1 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wider transition-colors ${basemap === "sat" ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:text-slate-800"}`}><Satellite className="h-3 w-3" /> Sat</button>
          </div>
          <span className="flex items-center gap-1.5 font-mono text-[11px] tabular-nums text-slate-500"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> {clock} <span className="text-slate-300">UTC</span></span>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        {((d.corridor_alerts || []).length > 0 || (isAb && (d.alerts || []).length > 0)) && (
          closuresOpen ? (
            <div data-testid="gov-map-alerts" className="hidden w-72 flex-shrink-0 flex-col border-r border-slate-200 bg-white md:flex">
              <button data-testid="cc-closures-toggle" onClick={() => setClosuresOpen(false)} title="Collapse" className="flex flex-shrink-0 items-center gap-1.5 border-b border-red-100 bg-red-50 px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-widest text-red-700 transition-colors hover:bg-red-100">
                <TriangleAlert className="h-3.5 w-3.5" /> Corridor closures
                <span className="rounded-sm bg-red-200 px-1.5 tabular-nums text-red-800">{(d.corridor_alerts || []).length + (isAb ? (d.alerts || []).length : 0)}</span>
                <PanelLeftClose className="ml-auto h-3.5 w-3.5" />
              </button>
              <div className="space-y-1.5 overflow-y-auto p-2">
                {(d.corridor_alerts || []).map((a) => (
                  <button key={`ca-${a.id}`} type="button" onClick={() => a.lat != null && setFlyTarget({ lat: a.lat, lng: a.lng, id: a.id, t: Date.now() })} data-testid="gov-corridor-alert" className={`block w-full rounded-sm border border-red-100 bg-white px-2.5 py-2 text-left text-xs leading-snug text-slate-700 transition-colors ${a.lat != null ? "cursor-pointer hover:border-red-300 hover:bg-red-50" : "cursor-default"}`}>
                    <span className="font-bold text-red-600">Corridor closure · {a.corridor}</span> — {a.headline}{a.road ? ` (${a.road})` : ""}{a.state ? ` · ${a.state}` : ""}
                    {a.lat != null && <span className="mt-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-red-500/70">▸ Click to locate on map</span>}
                  </button>
                ))}
                {isAb && (d.alerts || []).map((a) => (
                  <div key={`al-${a.id}`} className="rounded-sm border border-[#FF5722]/20 bg-white px-2.5 py-2 text-xs leading-snug text-slate-700">
                    <span className="font-semibold text-[#FF5722]">Advisory</span> — {a.message}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="hidden w-10 flex-shrink-0 flex-col items-center border-r border-slate-200 bg-white md:flex">
              <button data-testid="cc-closures-toggle" onClick={() => setClosuresOpen(true)} title="Show corridor closures" className="flex w-full flex-col items-center gap-2 py-3 text-red-700 transition-colors hover:bg-red-50">
                <PanelLeftOpen className="h-4 w-4" />
                <TriangleAlert className="h-4 w-4" />
                <span className="rounded-sm bg-red-200 px-1 py-0.5 font-mono text-[10px] font-bold tabular-nums text-red-800">{(d.corridor_alerts || []).length + (isAb ? (d.alerts || []).length : 0)}</span>
              </button>
            </div>
          )
        )}

        <div data-testid="gov-map" className="relative min-w-0 flex-1 bg-slate-100">
          <div data-testid="cc-layers-panel" className="absolute left-4 top-4 z-[1000] hidden w-56 flex-col overflow-hidden rounded-sm border border-slate-200 bg-white/95 shadow-xl backdrop-blur md:flex" style={{ maxHeight: "calc(100% - 2rem)" }}>
            <button data-testid="cc-layers-toggle" onClick={() => setLayersOpen((v) => !v)} className="flex flex-shrink-0 items-center gap-1.5 border-b border-slate-100 px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500 transition-colors hover:bg-slate-50"><Layers className="h-3.5 w-3.5" /> Layers {layersOpen ? <ChevronUp className="ml-auto h-3.5 w-3.5" /> : <ChevronDown className="ml-auto h-3.5 w-3.5" />}</button>
            {layersOpen && (
              <>
                <div className="border-b border-slate-100 p-2">
                  <div className="flex items-center gap-1.5 rounded-sm border border-slate-200 bg-white px-2">
                    <Search className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                    <input data-testid="cc-layer-search" value={layerQuery} onChange={(e) => setLayerQuery(e.target.value)} placeholder="Search layers…" className="h-7 w-full bg-transparent text-xs outline-none placeholder:text-slate-400" />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-1.5">
                  {visibleLayers.map((lt) => {
                    const Icon = lt.icon; const on = layers[lt.key];
                    return (
                      <button key={lt.key} data-testid={`cc-layer-${lt.key}`} onClick={() => toggle(lt.key)} className="flex w-full items-center gap-2.5 rounded-sm px-2 py-1.5 text-left transition-colors hover:bg-slate-50">
                        <span className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-sm border ${on ? "border-transparent" : "border-slate-300 bg-white"}`} style={on ? { backgroundColor: lt.color } : {}}>{on && <span className="h-1.5 w-1.5 rounded-[1px] bg-white" />}</span>
                        <Icon className="h-4 w-4 flex-shrink-0" style={{ color: on ? lt.color : "#94a3b8" }} />
                        <span className={`flex-1 truncate text-xs font-medium ${on ? "text-slate-900" : "text-slate-500"}`}>{lt.label}</span>
                        <span className="font-mono text-[10px] tabular-nums text-slate-400">{layerCount[lt.key] ?? 0}</span>
                      </button>
                    );
                  })}
                  {visibleLayers.length === 0 && <p className="py-4 text-center font-mono text-[11px] text-slate-400">No layers match.</p>}
                </div>
                <div className="grid flex-shrink-0 grid-cols-2 gap-px border-t border-slate-200 bg-slate-200">
                  {kpis.map((kk) => (
                    <div key={kk.l} className="bg-white px-3 py-2">
                      <p className={`font-mono text-lg font-medium leading-none tracking-tight ${kk.hot ? "text-red-600" : "text-slate-900"}`}>{kk.n}</p>
                      <p className="mt-1 font-mono text-[9px] font-semibold uppercase tracking-wider text-slate-400">{kk.l}</p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <MapContainer key={region} center={center} zoom={region === "au" ? 5 : 6} zoomControl={false} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          {basemap === "sat" ? (
            <>
              <TileLayer attribution='Imagery &copy; Esri' url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
              <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}" />
            </>
          ) : (
            <>
              <TileLayer attribution='Tiles &copy; Esri' url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}" />
              <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}" />
            </>
          )}
          <ZoomControl position="bottomright" />
          <FlyController target={flyTarget} />
          {(d.corridors || []).map((c) => (
            <Polyline key={c.name} positions={c.line} pathOptions={{ color: BAND_COLOR[c.band] || "#10b981", weight: 6, opacity: 0.9 }}>
              <MapTooltip>{c.name} — {c.condition} · risk {c.band} ({c.score}/100)</MapTooltip>
            </Polyline>
          ))}
          <WinterRoadsLayer segments={d.winter_roads} enabled={layers.winter} />
          <AuRoadNetworkLayer ways={roadNet} enabled={!isAb && layers.network} />
          <AuHazardOverlayLayer red={hazardWays.red} amber={hazardWays.amber} enabled={!isAb && layers.network && layers.events} />
          <ClusterLayer items={layers.rwis ? (d.weather_stations || []) : []} enabled={layers.rwis} radius={4} stroke="#0f766e" fill="#14b8a6"
            tooltip={(s) => `RWIS · air ${s.air_temp ?? "n/a"}°C · pavement ${s.pavement_temp ?? "n/a"}°C · wind ${s.wind_speed ?? "n/a"} km/h`} />
          <ClusterLayer items={layers.cameras ? (d.cameras || []) : []} enabled={layers.cameras} radius={4} stroke="#0369a1" fill="#0ea5e9"
            popupHtml={(cam) => { const esc = (s) => String(s || "").replace(/[&<>"']/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[m])); const title = cam.roadway || cam.name || "Camera"; const sub = cam.location || cam.direction || ""; const badge = cam.state ? `<span style="display:inline-block;margin:0 0 6px;padding:1px 7px;border-radius:999px;background:#0A234A;color:#fff;font-size:10px;font-weight:700">${esc(cam.state)}</span>` : ""; return `<div style="width:230px">${badge}<p style="margin:0 0 2px;font-weight:700;color:#0A234A;font-size:12px">${esc(title)}</p>${sub ? `<p style="margin:0 0 6px;color:#0A234A;opacity:.6;font-size:11px">${esc(sub)}</p>` : ""}<img src="${esc(cam.image)}" loading="lazy" style="width:100%;border-radius:10px" onerror="this.style.display='none'"/></div>`; }} />
          <ClusterLayer items={layers.events ? (d.road_events || []) : []} enabled={layers.events} radius={5} stroke="#7f1d1d" fill="#dc2626"
            tooltip={(e) => `${e.full_closure ? "FULL CLOSURE · " : ""}${e.type || ""}${e.subtype ? ` · ${e.subtype}` : ""} — ${e.headline || ""}${e.road ? ` (${e.road})` : ""}`} />
          {(d.emergencies || []).map((e) => (
            <CircleMarker key={`em-${e.id}`} center={[e.lat, e.lng]} radius={6} pathOptions={{ color: "#4c1d95", weight: 1, fillColor: e.severity === "high" ? "#dc2626" : "#7c3aed", fillOpacity: 0.9 }}>
              <MapTooltip>{e.agency}: {e.title} ({e.category})</MapTooltip>
            </CircleMarker>
          ))}
          {(d.zones || []).map((z) => (
            <CircleMarker key={`z-${z.id}`} center={[z.lat, z.lng]} radius={9} pathOptions={{ color: "#ef4444", fillColor: "#ef4444", fillOpacity: 0.35 }}>
              <MapTooltip>{z.name} (restricted impound)</MapTooltip>
            </CircleMarker>
          ))}
          {cands.map((v) => {
            const zoneList = d.zones || [];
            const z = v.suggested_zone && zoneList.find((zz) => zz.id === v.suggested_zone.zone_id || zz.name === v.suggested_zone.name);
            return (
              <span key={`flag-${v.vehicle_ref}`}>
                {v.latitude != null && z && (
                  <Polyline positions={[[v.latitude, v.longitude], [z.lat, z.lng]]} pathOptions={{ color: "#FF5722", weight: 2.5, opacity: 0.9, dashArray: "6 6" }}>
                    <MapTooltip>Tow route · {v.vehicle_ref} → {z.name}{v.suggested_zone.distance_km != null ? ` (~${v.suggested_zone.distance_km} km)` : ""}</MapTooltip>
                  </Polyline>
                )}
                {v.latitude != null && (
                  <CircleMarker center={[v.latitude, v.longitude]} radius={8} pathOptions={{ color: "#FF5722", weight: 3, fillColor: "#FFB59B", fillOpacity: 1 }}>
                    <MapTooltip>FLAGGED · {v.vehicle_ref} — {v.reason}</MapTooltip>
                  </CircleMarker>
                )}
              </span>
            );
          })}
          {(d.facilities || []).map((f) => (
            <CircleMarker key={`f-${f.id}`} center={[f.lat, f.lng]} radius={7} pathOptions={{ color: "#0A234A", weight: 1.5, fillColor: "#0A234A", fillOpacity: 0.85 }}>
              <MapTooltip>{f.name} — {f.type}{f.occupancy != null ? ` · ${f.occupancy}% occ` : ""}</MapTooltip>
            </CircleMarker>
          ))}
          {(d.vehicles || []).map((v) => (
            <CircleMarker key={`v-${v.id}`} center={[v.lat, v.lng]} radius={6} pathOptions={{ color: "#fff", weight: 2, fillColor: HOS_COLOR[v.hos_state] || "#94a3b8", fillOpacity: 1 }}>
              <MapTooltip>{v.name} — {String(v.hos_state).replace(/_/g, " ")}, {v.speed_kph ?? 0} km/h</MapTooltip>
            </CircleMarker>
          ))}
        </MapContainer>

        <div className="absolute bottom-4 left-4 z-[1000]">
          <button data-testid="cc-legend-toggle" onClick={() => setLegendOpen((v) => !v)} className="flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white/95 px-2.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-600 shadow backdrop-blur transition-colors hover:text-[#FF5722]">
            <MapPin className="h-3.5 w-3.5 text-[#FF5722]" /> Legend {legendOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
          </button>
          {legendOpen && (
            <div data-testid="gov-map-legend" className="mt-2 grid max-h-[45vh] w-56 grid-cols-1 gap-y-1.5 overflow-y-auto rounded-sm border border-slate-200 bg-white/95 p-3 shadow-lg backdrop-blur">
              {legendItems.map((it, i) => (
                <span key={i} className="flex items-center gap-2 text-[11px] font-medium text-slate-600">
                  {it.t === "line" && <span className="h-1 w-4 flex-shrink-0 rounded-full" style={{ background: it.c }} />}
                  {it.t === "dot" && <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ background: it.c }} />}
                  {it.t === "ring" && <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full bg-white" style={{ boxShadow: `0 0 0 2px ${it.c}` }} />}
                  {it.label}
                </span>
              ))}
            </div>
          )}
        </div>
        </div>

        <div data-testid="cc-right-rail" className="hidden w-[360px] flex-shrink-0 flex-col overflow-y-auto border-l border-slate-200 bg-white xl:flex">
          <LiveDisruptionsPanel events={d.road_events || []} onLocate={(e) => setFlyTarget({ lat: e.lat, lng: e.lng, id: e.id, t: Date.now() })} />
          <LiveCamerasPanel cameras={d.cameras || []} />
          <div className="border-b border-slate-200 p-2"><AnnouncementsPanel /></div>
        </div>
      </div>

      <div data-testid="cc-status-bar" className="flex h-7 flex-shrink-0 items-center gap-4 overflow-x-auto border-t border-slate-200 bg-white px-4 font-mono text-[10px] text-slate-400">
        {sources.map((s) => (
          <span key={s.id} data-testid={s.testid || undefined} title={s.detail} className="flex flex-shrink-0 items-center gap-1.5">
            <span className={`h-1.5 w-1.5 rounded-full ${s.ok ? "bg-emerald-500" : "bg-slate-300"}`} />
            <span className="font-semibold uppercase tracking-wider text-slate-500">{s.label}</span>
            <span className="max-w-[160px] truncate text-slate-400">{s.detail}</span>
          </span>
        ))}
        <span className="ml-auto flex flex-shrink-0 items-center gap-3 whitespace-nowrap">
          <span>{(d.road_events || []).length} events · {closures} closures · {(d.cameras || []).length} cameras</span>
          {updated && <span className="text-slate-300">updated {timeAgo(updated.toISOString())} ago</span>}
        </span>
      </div>
    </div>
  );
}

function RailSection({ title, icon: Icon, count, headerRight, children }) {
  return (
    <div className="flex flex-col border-b border-slate-200">
      <div className="flex items-center justify-between gap-2 bg-slate-50 px-3 py-2">
        <span className="flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-700">
          <Icon className="h-3.5 w-3.5 text-[#FF5722]" /> {title}
          {count != null && <span className="rounded-sm bg-slate-200 px-1.5 font-mono text-[10px] tabular-nums text-slate-600">{count}</span>}
        </span>
        {headerRight}
      </div>
      {children}
    </div>
  );
}

function LiveDisruptionsPanel({ events, onLocate }) {
  const [tab, setTab] = useState("ALL");
  const states = useMemo(() => [...new Set(events.map((e) => e.state).filter(Boolean))].sort(), [events]);
  const sevRank = { high: 3, medium: 2, low: 1 };
  const list = useMemo(() => events
    .filter((e) => tab === "ALL" || e.state === tab)
    .slice().sort((a, b) => (b.full_closure ? 1 : 0) - (a.full_closure ? 1 : 0) || (sevRank[b.severity] || 0) - (sevRank[a.severity] || 0))
    .slice(0, 80), [events, tab]);
  return (
    <RailSection title="Live Disruptions" icon={AlertTriangle} count={events.length} headerRight={<DataTag mode="live" />}>
      {states.length > 0 && (
        <div className="flex gap-1 overflow-x-auto border-b border-slate-100 px-2 py-1.5">
          {["ALL", ...states].map((s) => (
            <button key={s} data-testid={`cc-disruption-tab-${s}`} onClick={() => setTab(s)} className={`flex-shrink-0 rounded-sm px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider transition-colors ${tab === s ? "bg-[#FF5722] text-white" : "bg-slate-100 text-slate-500 hover:text-slate-800"}`}>{s}</button>
          ))}
        </div>
      )}
      <div className="max-h-[38vh] overflow-y-auto">
        {list.length === 0 && <p className="py-6 text-center font-mono text-[11px] text-slate-400">No live disruptions.</p>}
        {list.map((e) => (
          <button key={e.id} type="button" onClick={() => e.lat != null && onLocate?.(e)} data-testid="cc-disruption-row" className={`flex w-full items-start gap-2 border-b border-slate-50 px-3 py-2 text-left transition-colors ${e.lat != null ? "cursor-pointer hover:bg-slate-50" : "cursor-default"}`}>
            <span className={`mt-1 h-2 w-2 flex-shrink-0 rounded-full ${e.full_closure ? "bg-red-600" : e.severity === "high" ? "bg-orange-500" : e.severity === "medium" ? "bg-amber-500" : "bg-slate-300"}`} />
            <div className="min-w-0">
              <p className="truncate text-xs font-semibold text-slate-900">{e.full_closure && <span className="text-red-600">CLOSED · </span>}{e.headline || e.type || "Road event"}</p>
              <p className="truncate font-mono text-[10px] text-slate-400">{[e.road, e.type, e.state].filter(Boolean).join(" · ")}{e.lat != null ? "  ▸ locate" : ""}</p>
            </div>
          </button>
        ))}
      </div>
    </RailSection>
  );
}

function LiveCamerasPanel({ cameras }) {
  const [tab, setTab] = useState("ALL");
  const [active, setActive] = useState(null);
  const [bust, setBust] = useState(Date.now());
  const [wallOpen, setWallOpen] = useState(false);
  const [wall, setWall] = useState([]);
  const [wallBust, setWallBust] = useState(Date.now());
  const states = useMemo(() => [...new Set(cameras.map((c) => c.state).filter(Boolean))].sort(), [cameras]);
  const withImg = useMemo(() => cameras.filter((c) => c.image), [cameras]);
  const list = useMemo(() => withImg.filter((c) => tab === "ALL" || c.state === tab).slice(0, 60), [withImg, tab]);
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => setBust(Date.now()), 15000);
    return () => clearInterval(t);
  }, [active]);
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setActive(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);
  const bustUrl = (url) => (url ? `${url}${url.includes("?") ? "&" : "?"}_cb=${bust}` : url);
  useEffect(() => {
    if (!wallOpen) return undefined;
    const t = setInterval(() => setWallBust(Date.now()), 15000);
    const onKey = (e) => { if (e.key === "Escape") setWallOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => { clearInterval(t); window.removeEventListener("keydown", onKey); };
  }, [wallOpen]);
  const toggleWall = (cam) => setWall((w) => {
    const key = cam.image;
    if (w.some((x) => x.image === key)) return w.filter((x) => x.image !== key);
    if (w.length >= 6) return w;
    return [...w, cam];
  });
  const wallUrl = (url) => (url ? `${url}${url.includes("?") ? "&" : "?"}_cb=${wallBust}` : url);
  const inWall = (cam) => wall.some((x) => x.image === cam.image);
  return (
    <RailSection title="Live Cameras" icon={Video} count={withImg.length} headerRight={
      <div className="flex items-center gap-1.5">
        <button data-testid="cc-wall-open" onClick={() => setWallOpen(true)} title="Video wall" className="flex items-center gap-1 rounded-sm border border-slate-300 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-slate-600 transition-colors hover:border-[#FF5722] hover:text-[#FF5722]"><LayoutGrid className="h-3 w-3" /> Wall{wall.length ? ` ${wall.length}` : ""}</button>
        <DataTag mode="live" />
      </div>
    }>
      {states.length > 0 && (
        <div className="flex gap-1 overflow-x-auto border-b border-slate-100 px-2 py-1.5">
          {["ALL", ...states].map((s) => (
            <button key={s} data-testid={`cc-camera-tab-${s}`} onClick={() => setTab(s)} className={`flex-shrink-0 rounded-sm px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider transition-colors ${tab === s ? "bg-[#FF5722] text-white" : "bg-slate-100 text-slate-500 hover:text-slate-800"}`}>{s}</button>
          ))}
        </div>
      )}
      <div className="grid max-h-[34vh] grid-cols-2 gap-1.5 overflow-y-auto p-2">
        {list.length === 0 && <p className="col-span-2 py-6 text-center font-mono text-[11px] text-slate-400">No cameras available.</p>}
        {list.map((c, i) => (
          <button key={i} type="button" onClick={() => { setBust(Date.now()); setActive(c); }} data-testid="cc-camera-tile" className="group relative flex h-20 items-center justify-center overflow-hidden rounded-sm border border-slate-200 bg-slate-800 text-left">
            <img src={c.image} loading="lazy" alt={c.roadway || c.name || "camera"} className="h-full w-full object-cover opacity-90 transition-opacity group-hover:opacity-100" onError={(ev) => { ev.currentTarget.style.visibility = "hidden"; }} />
            <Video className="pointer-events-none absolute h-4 w-4 text-slate-500" />
            <span className="pointer-events-none absolute inset-x-0 bottom-0 truncate bg-black/60 px-1.5 py-0.5 font-mono text-[9px] font-semibold text-white">{c.roadway || c.name || c.location || "CCTV"}</span>
          </button>
        ))}
      </div>

      {active && (
        <div data-testid="cc-camera-lightbox" onClick={() => setActive(null)} className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl overflow-hidden rounded-sm border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-4 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-slate-900">{active.roadway || active.name || "CCTV camera"}</p>
                <p className="truncate font-mono text-[11px] text-slate-400">{[active.location, active.direction, active.state].filter(Boolean).join(" · ") || "Live traffic camera"}</p>
              </div>
              <div className="flex flex-shrink-0 items-center gap-2">
                <span className="flex items-center gap-1.5 rounded-sm border border-emerald-300 bg-emerald-50 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-emerald-700"><RefreshCw className="h-3 w-3 animate-spin" style={{ animationDuration: "3s" }} /> Auto-refresh 15s</span>
                <button data-testid="cc-camera-refresh" onClick={() => setBust(Date.now())} title="Refresh now" className="flex h-7 w-7 items-center justify-center rounded-sm border border-slate-200 text-slate-500 transition-colors hover:border-[#FF5722] hover:text-[#FF5722]"><RefreshCw className="h-3.5 w-3.5" /></button>
                <button data-testid="cc-camera-close" onClick={() => setActive(null)} title="Close" className="flex h-7 w-7 items-center justify-center rounded-sm border border-slate-200 text-slate-500 transition-colors hover:border-red-300 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <div className="flex items-center justify-center bg-slate-900" style={{ minHeight: 320 }}>
              <img key={bust} src={bustUrl(active.image)} alt={active.roadway || active.name || "camera"} className="max-h-[70vh] w-full object-contain" onError={(ev) => { ev.currentTarget.style.opacity = "0.3"; }} />
            </div>
            <div className="flex items-center justify-between border-t border-slate-200 px-4 py-2 font-mono text-[10px] text-slate-400">
              <span>{active.state ? `${active.state} · ` : ""}Live traffic camera</span>
              <span>updated {new Date(bust).toISOString().slice(11, 19)} UTC</span>
            </div>
          </div>
        </div>
      )}

      {wallOpen && (
        <div data-testid="cc-camera-wall" onClick={() => setWallOpen(false)} className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/75 p-4 backdrop-blur-sm">
          <div onClick={(e) => e.stopPropagation()} className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-sm border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-4 py-2.5">
              <p className="flex items-center gap-1.5 text-sm font-bold text-slate-900"><LayoutGrid className="h-4 w-4 text-[#FF5722]" /> Camera Video Wall <span className="font-mono text-[11px] font-normal text-slate-400">· {wall.length}/6 feeds</span></p>
              <div className="flex flex-shrink-0 items-center gap-2">
                <span className="flex items-center gap-1.5 rounded-sm border border-emerald-300 bg-emerald-50 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-emerald-700"><RefreshCw className="h-3 w-3 animate-spin" style={{ animationDuration: "3s" }} /> Auto-refresh 15s</span>
                <button data-testid="cc-wall-refresh" onClick={() => setWallBust(Date.now())} title="Refresh now" className="flex h-7 w-7 items-center justify-center rounded-sm border border-slate-200 text-slate-500 transition-colors hover:border-[#FF5722] hover:text-[#FF5722]"><RefreshCw className="h-3.5 w-3.5" /></button>
                <button data-testid="cc-wall-close" onClick={() => setWallOpen(false)} title="Close" className="flex h-7 w-7 items-center justify-center rounded-sm border border-slate-200 text-slate-500 transition-colors hover:border-red-300 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto bg-slate-900 p-2">
              {wall.length === 0 ? (
                <p className="py-16 text-center font-mono text-[12px] text-slate-400">Pick 4–6 feeds below to build your wall.</p>
              ) : (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {wall.map((c, i) => (
                    <div key={i} data-testid="cc-wall-tile" className="group relative overflow-hidden rounded-sm border border-slate-700 bg-black">
                      <img key={wallBust} src={wallUrl(c.image)} alt={c.roadway || c.name || "camera"} className="h-40 w-full object-cover" onError={(ev) => { ev.currentTarget.style.opacity = "0.3"; }} />
                      <span className="absolute inset-x-0 bottom-0 truncate bg-black/70 px-2 py-1 font-mono text-[10px] font-semibold text-white">{c.roadway || c.name || c.location || "CCTV"}{c.state ? ` · ${c.state}` : ""}</span>
                      <button data-testid="cc-wall-remove" onClick={() => toggleWall(c)} title="Remove" className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-sm bg-black/60 text-white opacity-0 transition-opacity hover:bg-red-600 group-hover:opacity-100"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex-shrink-0 border-t border-slate-200 p-2">
              <p className="mb-1.5 px-1 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">Add feeds ({tab})</p>
              <div className="flex gap-1.5 overflow-x-auto pb-1">
                {list.map((c, i) => {
                  const on = inWall(c); const full = wall.length >= 6 && !on;
                  return (
                    <button key={i} data-testid="cc-wall-pick" onClick={() => toggleWall(c)} disabled={full} title={c.roadway || c.name || "camera"} className={`group relative h-14 w-24 flex-shrink-0 overflow-hidden rounded-sm border-2 ${on ? "border-[#FF5722]" : full ? "border-slate-200 opacity-40" : "border-slate-200 hover:border-slate-400"}`}>
                      <img src={c.image} loading="lazy" alt="" className="h-full w-full object-cover" onError={(ev) => { ev.currentTarget.style.visibility = "hidden"; }} />
                      {on && <span className="absolute inset-0 flex items-center justify-center bg-[#FF5722]/30"><span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#FF5722] text-white"><CheckCircle2 className="h-3.5 w-3.5" /></span></span>}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </RailSection>
  );
}

function Reports() {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/gov/report").then((res) => setR(res.data)).catch(() => setR(null)); }, []);
  if (!r) return <Loading />;
  const exportCsv = () => {
    const lines = [["Corridor", "Route", "Safety", "Incidents 30d", "Utilisation %", "Pressure"].join(",")];
    for (const c of r.corridors) lines.push([c.name, c.route, c.safety_score, c.incidents_30d, c.utilisation_pct, c.capacity_pressure].map((x) => `"${x}"`).join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "fleetpods_gov_report.csv"; a.click();
  };
  return (
    <Section title="Reports" subtitle="Public-benefit reporting for administrators and policy stakeholders.">
      <div className="mb-4 flex gap-2">
        <Button data-testid="gov-report-csv" onClick={exportCsv} className="rounded-xl bg-[#FF5722] font-bold text-white hover:bg-[#F4511E]"><Download className="mr-2 h-4 w-4" /> Export CSV</Button>
        <Button data-testid="gov-report-print" onClick={() => window.print()} variant="outline" className="rounded-xl"><FileText className="mr-2 h-4 w-4" /> Print / PDF</Button>
      </div>
      <div className={`${CARD} mb-6`}>
        <p className="text-sm text-neutral-500">Generated {new Date(r.generated_at).toLocaleString()} · {r.jurisdiction}</p>
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div><p className="text-2xl font-bold text-[#0A234A] font-display">{r.kpis.corridors}</p><p className="text-xs text-neutral-500">Corridors</p></div>
          <div><p className="text-2xl font-bold text-[#0A234A] font-display">{r.kpis.facilities}</p><p className="text-xs text-neutral-500">Facilities</p></div>
          <div><p className="text-2xl font-bold text-[#0A234A] font-display">{r.kpis.avg_safety}</p><p className="text-xs text-neutral-500">Avg safety</p></div>
          <div><p className="text-2xl font-bold text-[#0A234A] font-display">{r.kpis.open_exceptions}</p><p className="text-xs text-neutral-500">Open exceptions</p></div>
        </div>
      </div>
      <div className={CARD}><p className="mb-3 font-display text-lg font-bold text-[#0A234A]">Recent access (audit)</p>
        <div className="space-y-1 text-sm">
          {r.audit_recent.map((a, i) => (
            <div key={i} className="flex flex-wrap items-center gap-x-3 border-b border-neutral-100 py-1.5">
              <span className="text-neutral-400 text-xs whitespace-nowrap">{new Date(a.ts).toLocaleString()}</span>
              <span className="text-neutral-600">{a.actor_email}</span>
              <span className="font-semibold text-[#0A234A]">{a.action}</span>
              <span className="text-neutral-500">{a.resource}</span>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

const wxBand = (b) => ({ low: "bg-emerald-50 text-emerald-700", moderate: "bg-amber-50 text-amber-700", high: "bg-orange-50 text-orange-700", severe: "bg-red-50 text-red-700" }[b] || "bg-neutral-100 text-neutral-600");

function CorridorDetail({ id, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/gov/corridors/${id}`).then((r) => setD(r.data)).catch(() => setD(null)); }, [id]);
  return (
    <div data-testid="gov-corridor-detail" className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-[#0A234A]/40 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="mt-10 w-full max-w-3xl rounded-3xl bg-white p-6 shadow-2xl sm:p-8" onClick={(e) => e.stopPropagation()}>
        {!d ? <Loading /> : (
          <>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-[#FF5722]">{d.corridor.route}</p>
                <h2 className="font-display text-2xl font-bold text-[#0A234A]">{d.corridor.name}</h2>
              </div>
              <button data-testid="gov-corridor-detail-close" onClick={onClose} className="rounded-lg bg-neutral-100 px-3 py-1.5 text-sm font-semibold text-neutral-600 hover:bg-neutral-200">Close</button>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className={CARD}><p className="text-2xl font-bold text-[#0A234A] font-display">{d.corridor.safety_score}</p><p className="text-xs text-neutral-500">Safety index</p></div>
              <div className={CARD}><p className="text-2xl font-bold text-[#0A234A] font-display">{d.corridor.incidents_30d}</p><p className="text-xs text-neutral-500">Incidents 30d</p></div>
              <div className={CARD}><p className="text-2xl font-bold text-[#0A234A] font-display">{d.corridor.utilisation_pct}%</p><p className="text-xs text-neutral-500">Utilisation</p></div>
              <div className={CARD}><p className="text-2xl font-bold text-[#0A234A] font-display">{d.vehicles.length}</p><p className="text-xs text-neutral-500">Units on corridor</p></div>
            </div>

            <div className={`${CARD} mt-4`}>
              <div className="flex items-center justify-between">
                <p className="font-display font-bold text-[#0A234A]">Live conditions & driving risk</p>
                <span className={`rounded-full px-2.5 py-1 text-xs font-bold capitalize ${wxBand(d.weather.band)}`}>{d.weather.band} · {d.weather.score}/100</span>
              </div>
              <p className="mt-2 text-sm text-neutral-600">{d.weather.condition} · {d.weather.temperature_c}°C · wind {Math.round(d.weather.wind_kph || 0)} km/h · visibility {d.weather.visibility_m != null ? Math.round(d.weather.visibility_m / 1000) + " km" : "—"}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">{(d.weather.factors || []).map((f, i) => <span key={i} className="rounded-full bg-[#0A234A]/5 px-2 py-0.5 text-xs font-semibold text-[#0A234A]">{f}</span>)}</div>
              <p className="mt-1 text-xs text-neutral-400">Source: Open-Meteo (public, live) · {d.weather.observed || ""}</p>
            </div>

            <div className={`${CARD} mt-4`}>
              <p className="mb-2 font-display font-bold text-[#0A234A]">Safety trend (14 days)</p>
              <div style={{ width: "100%", height: 160 }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <AreaChart data={d.trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <defs><linearGradient id="cg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#FF5722" stopOpacity={0.35} /><stop offset="100%" stopColor="#FF5722" stopOpacity={0} /></linearGradient></defs>
                    <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#94a3b8" }} tickFormatter={(x) => x.slice(5)} /><YAxis tick={{ fontSize: 9, fill: "#94a3b8" }} /><Tooltip />
                    <Area type="monotone" dataKey="safety_index" stroke="#FF5722" strokeWidth={2} fill="url(#cg)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className={CARD}>
                <p className="mb-2 font-display font-bold text-[#0A234A]">Incidents ({d.exceptions.length})</p>
                {d.exceptions.length === 0 ? <p className="text-sm text-neutral-400">None on this corridor.</p> :
                  d.exceptions.map((e) => <div key={e.exception_id} className="flex items-center gap-2 border-b border-neutral-100 py-1.5 text-sm"><span className={`rounded-full px-2 py-0.5 text-xs font-bold capitalize ${sevColor(e.severity)}`}>{e.severity}</span><span className="text-neutral-700">{e.kind}</span></div>)}
              </div>
              <div className={CARD}>
                <p className="mb-2 font-display font-bold text-[#0A234A]">Units on corridor</p>
                {d.vehicles.length === 0 ? <p className="text-sm text-neutral-400">No units currently reporting.</p> :
                  d.vehicles.map((v) => <div key={v.id} className="flex items-center justify-between border-b border-neutral-100 py-1.5 text-sm"><span className="font-semibold text-[#0A234A]">{v.name}</span><span className="capitalize text-neutral-500">{String(v.hos_state).replace(/_/g, " ")}</span></div>)}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CorridorRows({ corridors }) {
  const [sel, setSel] = useState(null);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead><tr className="border-b border-slate-200 font-mono text-[10px] uppercase tracking-wider text-slate-500">
          <th className="px-2 pb-2 font-semibold">Corridor</th><th className="px-2 pb-2 font-semibold">Route</th><th className="px-2 pb-2 text-right font-semibold">Safety</th><th className="px-2 pb-2 text-right font-semibold">Inc 30d</th><th className="px-2 pb-2 font-semibold">Live now</th><th className="px-2 pb-2 font-semibold">Pressure</th>
        </tr></thead>
        <tbody>
          {(corridors || []).map((c) => (
            <tr key={c.corridor_id} data-testid="gov-corridor-row" onClick={() => setSel(c.corridor_id)} className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-[#FF5722]/5">
              <td className="px-2 py-2.5 font-semibold text-slate-900">{c.name}</td>
              <td className="px-2 py-2.5 text-slate-500">{c.route}</td>
              <td className="px-2 py-2.5 text-right font-mono font-medium text-slate-900">{c.safety_score ?? "\u2014"}</td>
              <td className="px-2 py-2.5 text-right font-mono text-slate-600">{c.incidents_30d ?? "\u2014"}</td>
              <td className="px-2 py-2.5" data-testid="gov-corridor-live">
                <span className="font-mono font-semibold text-slate-900">{c.live_events ?? 0}</span>
                <span className="text-slate-400"> live</span>
                {c.live_closures > 0 && <span className="ml-2 rounded-sm border border-red-200 bg-red-50 px-1.5 py-0.5 font-mono text-[10px] font-bold text-red-700">{c.live_closures} closure{c.live_closures > 1 ? "s" : ""}</span>}
              </td>
              <td className="px-2 py-2.5"><span className={`rounded-sm px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${pressureColor(c.live_pressure || c.capacity_pressure)}`}>{c.live_pressure || c.capacity_pressure}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
      {sel && <CorridorDetail id={sel} onClose={() => setSel(null)} />}
    </div>
  );
}

function Corridors() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/gov/corridors").then((r) => setD(r.data.corridors)).catch(() => setD([])); }, []);
  if (!d) return <Loading />;
  return <Section title="Corridor Intelligence" subtitle="Ranked by safety score (lowest first)."><div className={CARD}><CorridorRows corridors={d} /></div></Section>;
}

function Facilities() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/gov/facilities").then((r) => setD(r.data.facilities)).catch(() => setD([])); }, []);
  if (!d) return <Loading />;
  return (
    <Section title="Facilities" subtitle="Utilisation and security posture across the network.">
      <div className="grid gap-4 sm:grid-cols-2">
        {d.map((f) => (
          <div key={f.facility_id} data-testid="gov-facility-card" className={CARD}>
            <div className="flex items-center justify-between">
              <p className="font-display font-bold text-[#0A234A]">{f.name}</p>
              <span className={`rounded-full px-2.5 py-1 text-xs font-bold capitalize ${pressureColor(f.security_level)}`}>{f.security_level} security</span>
            </div>
            <p className="text-sm text-neutral-500">{f.type} · {f.location}</p>
            <div className="mt-3 flex items-center justify-between text-sm"><span className="text-neutral-500">Occupancy</span><span className="font-bold text-[#0A234A]">{f.occupancy_pct}%</span></div>
            <Bar pct={f.occupancy_pct} />
            <p className="mt-2 text-xs text-neutral-500">{f.bays} bays · {f.pods} pods</p>
            <div className="mt-3 flex flex-wrap gap-1.5">{(f.features || []).map((x) => <span key={x} className="rounded-full bg-[#0A234A]/5 px-2 py-0.5 text-xs font-semibold text-[#0A234A]">{x}</span>)}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Compliance() {
  const [d, setD] = useState(null);
  const [receipts, setReceipts] = useState(null);
  const [subj, setSubj] = useState("");
  const [basis, setBasis] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const loadR = useCallback(() => { api.get("/gov/compliance/receipts").then((r) => setReceipts(r.data.receipts || [])).catch(() => setReceipts([])); }, []);
  useEffect(() => { api.get("/gov/compliance").then((r) => setD(r.data)).catch(() => setD({ bands: {}, corridors: [] })); loadR(); }, [loadR]);
  const issue = async (e) => {
    e.preventDefault();
    if (!subj.trim() || !basis.trim()) { setMsg("Subject reference and lawful basis are required."); return; }
    setBusy(true); setMsg("");
    try {
      const r = await api.post("/gov/compliance/receipts", { subject_ref: subj.trim(), lawful_basis: basis.trim() });
      setMsg(`Receipt #${r.data.chain_index} issued · SHA-256 ${r.data.content_hash.slice(0, 16)}… (provenance: ${r.data.provenance})`);
      setSubj(""); setBasis(""); loadR();
    } catch (err) { setMsg(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  const copyLink = (rid) => { const url = `${window.location.origin}/verify-receipt?r=${rid}`; navigator.clipboard?.writeText(url); setMsg(`Public verify link copied: ${url}`); };
  if (!d || !receipts) return <Loading />;
  const b = d.bands || {};
  const bands = [{ k: "green", label: "Green (85+)", cls: "bg-emerald-50 text-emerald-700" }, { k: "amber", label: "Amber (72-84)", cls: "bg-amber-50 text-amber-700" }, { k: "red", label: "Red (<72)", cls: "bg-red-50 text-red-700" }];
  const provCls = (p) => p === "live" ? "bg-emerald-50 text-emerald-700" : p === "sample" ? "bg-amber-50 text-amber-700" : "bg-neutral-100 text-neutral-500";
  return (
    <Section title="Compliance Assurance" subtitle="Safety-band distribution across corridors + tamper-evident cryptographic receipts.">
      <div className="grid grid-cols-3 gap-4">
        {bands.map((x) => <div key={x.k} className={`${CARD} text-center`}><p className={`inline-block rounded-full px-3 py-1 text-xs font-bold ${x.cls}`}>{x.label}</p><p className="mt-3 text-3xl font-bold text-[#0A234A] font-display">{b[x.k] ?? 0}</p></div>)}
      </div>
      <div className={`${CARD} mt-6`}><CorridorRows corridors={d.corridors} /></div>

      <div className={`${CARD} mt-6`}>
        <div className="mb-3 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-[#FF5722]" />
          <p className="font-display text-lg font-bold text-[#0A234A]">Cryptographic compliance receipts</p>
        </div>
        <p className="mb-4 text-sm text-neutral-500">Each receipt is a SHA-256 hash-chained record. Anyone can independently verify it at the public link — no login, no trust in us required.</p>
        <form onSubmit={issue} className="mb-5 grid gap-3 sm:grid-cols-4">
          <div className="space-y-1"><Label className="text-neutral-600 text-xs">Subject (vehicle ref)</Label><Input data-testid="gov-rcpt-subject" value={subj} onChange={(e) => setSubj(e.target.value)} placeholder="e.g. AB-TRK-5567" className="h-11 rounded-xl" /></div>
          <div className="space-y-1 sm:col-span-2"><Label className="text-neutral-600 text-xs">Lawful basis</Label><Input data-testid="gov-rcpt-basis" value={basis} onChange={(e) => setBasis(e.target.value)} placeholder="Statutory authority / audit ref" className="h-11 rounded-xl" /></div>
          <div className="flex items-end"><Button data-testid="gov-rcpt-issue" type="submit" disabled={busy} className="w-full rounded-xl bg-[#FF5722] font-bold text-white hover:bg-[#F4511E]">{busy ? "Issuing…" : "Issue receipt"}</Button></div>
        </form>
        {msg && <p data-testid="gov-rcpt-msg" className="mb-3 break-all text-sm font-semibold text-[#0A234A]">{msg}</p>}
        <div className="space-y-2">
          {receipts.length === 0 ? <p className="text-sm text-neutral-500">No receipts issued yet.</p> : receipts.map((r) => (
            <div key={r.receipt_id} data-testid="gov-rcpt-row" className="flex flex-wrap items-center gap-2 rounded-xl border border-neutral-100 bg-neutral-50/60 p-3 text-sm">
              <span className="font-mono text-xs font-bold text-[#0A234A]">#{r.chain_index}</span>
              <span className="font-display font-bold text-[#0A234A]">{r.subject_ref}</span>
              <span className={`rounded-full px-2 py-0.5 text-xs font-bold capitalize ${provCls(r.provenance)}`}>{r.provenance}</span>
              <span className="font-mono text-xs text-neutral-400">{r.content_hash.slice(0, 24)}…</span>
              <span className="ml-auto text-xs text-neutral-400">{new Date(r.issued_at).toLocaleString()}</span>
              <Button data-testid="gov-rcpt-copy" onClick={() => copyLink(r.receipt_id)} className="h-7 rounded-lg bg-[#0A234A] px-2.5 text-xs font-bold text-white hover:bg-[#0d2c5e]">Copy verify link</Button>
              <a data-testid="gov-rcpt-open" href={`/verify-receipt?r=${r.receipt_id}`} target="_blank" rel="noreferrer" className="text-xs font-bold text-[#FF5722] hover:underline">Open ↗</a>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

function Exceptions() {
  const [d, setD] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const load = useCallback(() => { api.get(`/gov/exceptions`).then((r) => setD(r.data)).catch(() => setD({ exceptions: [], auto_pending: 0 })); }, []);
  useEffect(() => { (async () => { try { await api.post(`/gov/exceptions/sync`); } catch {} load(); })(); }, [load]);
  const sync = async () => { setSyncing(true); try { await api.post(`/gov/exceptions/sync`); } catch {} load(); setSyncing(false); };
  if (!d) return <Loading />;
  const list = d.exceptions || [];
  return (
    <Section title="Exceptions" subtitle="Auto-populated from live emergency warnings, weather, road closures and telematics signals for your jurisdiction.">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Button data-testid="gov-exc-sync" onClick={sync} disabled={syncing} className="rounded-xl bg-[#FF5722] font-bold text-white hover:bg-[#F4511E]">
          {syncing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Sync signals now
        </Button>
        <span className="text-xs text-neutral-500">{list.length} exceptions · auto-generated from public + fleet signals</span>
      </div>
      <div className="space-y-3">
        {list.map((e) => (
          <div key={e.exception_id} data-testid="gov-exception-row" className={`${CARD} flex items-start gap-4`}>
            <span className={`mt-0.5 rounded-full px-2.5 py-1 text-xs font-bold capitalize ${sevColor(e.severity)}`}>{e.severity}</span>
            <div className="flex-1">
              <p className="font-semibold text-[#0A234A]">{e.kind} <span className="font-normal text-neutral-400">· {e.corridor}</span></p>
              <p className="text-sm text-neutral-500">{e.summary}</p>
            </div>
            <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${e.source === "auto" ? "bg-blue-50 text-blue-700" : "bg-neutral-100 text-neutral-600"}`}>{e.source === "auto" ? "auto" : "manual"}</span>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Zones() {
  const [zones, setZones] = useState(null);
  const [cands, setCands] = useState(null);
  const [form, setForm] = useState({ name: "", location: "", lawful_basis: "" });
  const [msg, setMsg] = useState("");
  const [tow, setTow] = useState(null);
  const [towBasis, setTowBasis] = useState("");
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState({});
  const [batchBasis, setBatchBasis] = useState("");
  const selCount = Object.values(sel).filter(Boolean).length;
  const loadZones = useCallback(() => { api.get("/gov/zones").then((r) => setZones(r.data.zones)).catch(() => setZones([])); }, []);
  const loadCands = useCallback(() => { api.get("/gov/impound/candidates").then((r) => setCands(r.data.candidates || [])).catch(() => setCands([])); }, []);
  useEffect(() => { loadZones(); loadCands(); }, [loadZones, loadCands]);

  const create = async (e) => {
    e.preventDefault(); setMsg("");
    try { await api.post("/gov/zones", form); setForm({ name: "", location: "", lawful_basis: "" }); loadZones(); setMsg("Zone created and logged to the audit trail."); }
    catch (err) { setMsg(formatApiError(err.response?.data?.detail) || err.message); }
  };
  const confirmTow = async () => {
    if (!towBasis.trim()) { setMsg("A lawful basis is required to impound."); return; }
    setBusy(true); setMsg("");
    try {
      const r = await api.post("/gov/impound/tow-in", { vehicle_ref: tow.vehicle_ref, reason: tow.reason, lawful_basis: towBasis.trim() });
      const d = r.data;
      setMsg(`${d.vehicle_ref} towed to ${d.zone.name}${d.distance_km != null ? ` (~${d.distance_km} km, nearest zone)` : ""}. Logged.`);
      setTow(null); setTowBasis(""); loadZones(); loadCands();
    } catch (err) { setMsg(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  const release = async (entry_id) => {
    const basis = window.prompt("Lawful basis for release (required, audited):");
    if (basis == null) return;
    if (!basis.trim()) { setMsg("A lawful basis is required to release."); return; }
    try { await api.post("/gov/impound/release", { entry_id, lawful_basis: basis.trim() }); loadZones(); loadCands(); setMsg("Vehicle released and logged."); }
    catch (err) { setMsg(formatApiError(err.response?.data?.detail) || err.message); }
  };
  const towBatch = async () => {
    const refs = cands.filter((v) => sel[v.vehicle_ref]);
    if (!refs.length) { setMsg("Select at least one vehicle."); return; }
    if (!batchBasis.trim()) { setMsg("A single lawful basis is required for the batch."); return; }
    setBusy(true); setMsg("");
    let ok = 0;
    for (const v of refs) {
      try { await api.post("/gov/impound/tow-in", { vehicle_ref: v.vehicle_ref, reason: v.reason, lawful_basis: batchBasis.trim() }); ok++; } catch {}
    }
    setMsg(`Batch impound: ${ok}/${refs.length} towed to their nearest zones under one lawful basis. Logged.`);
    setSel({}); setBatchBasis(""); loadZones(); loadCands(); setBusy(false);
  };

  if (!zones || !cands) return <Loading />;
  return (
    <Section title="Impound Zones (restricted)" subtitle="Access-controlled. Every tow-in / release requires a lawful basis and is audited.">
      <div className={`${CARD} mb-6`}>
        <div className="mb-3 flex items-center justify-between">
          <p className="flex items-center gap-2 font-display text-lg font-bold text-[#0A234A]"><TriangleAlert className="h-4 w-4 text-[#FF5722]" /> Tow-in worklist</p>
          <span className="text-xs font-semibold text-neutral-500">{cands.length} flagged · should not be on the road</span>
        </div>
        {selCount > 0 && (
          <div data-testid="gov-batch-bar" className="mb-3 flex flex-wrap items-end gap-2 rounded-xl border border-[#FF5722]/30 bg-[#FFF3EF] p-3">
            <div className="flex-1 space-y-1"><Label className="text-neutral-600 text-xs">Single lawful basis for {selCount} selected vehicle{selCount > 1 ? "s" : ""}</Label>
              <Input data-testid="gov-batch-basis" value={batchBasis} onChange={(e) => setBatchBasis(e.target.value)} placeholder="Statutory authority covering this batch" className="h-10 rounded-xl bg-white" /></div>
            <Button data-testid="gov-batch-tow" disabled={busy} onClick={towBatch} className="h-10 rounded-xl bg-[#FF5722] px-4 font-bold text-white hover:bg-[#F4511E]">{busy ? "Towing…" : `Tow ${selCount} selected`}</Button>
            <Button onClick={() => setSel({})} variant="ghost" className="h-10 rounded-xl px-3 text-neutral-500">Clear</Button>
          </div>
        )}
        {cands.length === 0 ? (
          <p className="text-sm text-neutral-500">No flagged vehicles right now.</p>
        ) : (
          <div className="space-y-2">
            {cands.map((v) => (
              <div key={v.vehicle_ref} data-testid="gov-tow-candidate" className="rounded-xl border border-neutral-100 bg-neutral-50/60 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <input data-testid="gov-cand-check" type="checkbox" checked={!!sel[v.vehicle_ref]} onChange={(e) => setSel((p) => ({ ...p, [v.vehicle_ref]: e.target.checked }))} className="h-4 w-4 accent-[#FF5722]" />
                  <Truck className="h-4 w-4 text-[#0A234A]" />
                  <span className="font-display font-bold text-[#0A234A]">{v.vehicle_ref}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-bold capitalize ${sevColor(v.severity)}`}>{v.severity}</span>
                  <span className="text-sm text-neutral-500">{v.reason}</span>
                  {v.corridor && <span className="text-xs text-neutral-400">· {v.corridor}</span>}
                  {v.suggested_zone && <span data-testid="gov-tow-suggest" className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">→ {v.suggested_zone.name}{v.suggested_zone.distance_km != null ? ` (~${v.suggested_zone.distance_km} km)` : ""}</span>}
                  <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide text-neutral-400">{v.source}</span>
                  <Button data-testid="gov-tow-btn" onClick={() => { setTow({ vehicle_ref: v.vehicle_ref, reason: v.reason }); setTowBasis(""); setMsg(""); }} className="h-8 rounded-lg bg-[#FF5722] px-3 text-xs font-bold text-white hover:bg-[#F4511E]">Tow to nearest zone</Button>
                </div>
                {tow?.vehicle_ref === v.vehicle_ref && (
                  <div data-testid="gov-tow-form" className="mt-3 flex flex-wrap items-end gap-2 border-t border-neutral-200 pt-3">
                    <div className="flex-1 space-y-1"><Label className="text-neutral-600 text-xs">Lawful basis (required)</Label>
                      <Input data-testid="gov-tow-basis" value={towBasis} onChange={(e) => setTowBasis(e.target.value)} placeholder="Statutory authority / out-of-service order ref" className="h-10 rounded-xl" /></div>
                    <Button data-testid="gov-tow-confirm" disabled={busy} onClick={confirmTow} className="h-10 rounded-xl bg-[#0A234A] px-4 font-bold text-white hover:bg-[#0d2c5e]">{busy ? "Towing…" : "Confirm impound"}</Button>
                    <Button onClick={() => setTow(null)} variant="ghost" className="h-10 rounded-xl px-3 text-neutral-500">Cancel</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {msg && <p data-testid="gov-impound-msg" className="mt-3 text-sm font-semibold text-[#0A234A]">{msg}</p>}
      </div>

      <form onSubmit={create} className={`${CARD} mb-6 grid gap-3 sm:grid-cols-4`}>
        <div className="space-y-1"><Label className="text-neutral-600 text-xs">Zone name</Label><Input data-testid="gov-zone-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Leduc Impound" className="h-11 rounded-xl" /></div>
        <div className="space-y-1"><Label className="text-neutral-600 text-xs">Location</Label><Input data-testid="gov-zone-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Leduc, AB" className="h-11 rounded-xl" /></div>
        <div className="space-y-1 sm:col-span-2"><Label className="text-neutral-600 text-xs">Lawful basis</Label><Input data-testid="gov-zone-basis" value={form.lawful_basis} onChange={(e) => setForm({ ...form, lawful_basis: e.target.value })} placeholder="Statutory authority / case reference" className="h-11 rounded-xl" /></div>
        <div className="sm:col-span-4"><Button data-testid="gov-zone-create" type="submit" className="rounded-xl bg-[#FF5722] font-bold text-white hover:bg-[#F4511E]">Create restricted zone</Button></div>
      </form>

      <div className="space-y-4">
        {zones.map((z) => (
          <div key={z.zone_id} data-testid="gov-zone-row" className={CARD}>
            <div className="flex items-center gap-2"><Lock className="h-4 w-4 text-[#FF5722]" /><p className="font-display font-bold text-[#0A234A]">{z.name}</p><span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-bold capitalize text-neutral-600">{z.status}</span></div>
            <p className="text-sm text-neutral-500">{z.location}{z.lat != null && z.lng != null && <span className="text-neutral-400"> · {z.lat.toFixed(2)}, {z.lng.toFixed(2)}</span>}</p>
            {(z.entries || []).length > 0 && (
              <div className="mt-3 space-y-1.5">
                {z.entries.map((en) => (
                  <div key={en.entry_id} className="flex items-center justify-between rounded-lg bg-neutral-50 px-3 py-2 text-sm">
                    <span className="font-semibold text-[#0A234A]">{en.vehicle_ref}</span>
                    <span className="flex-1 px-3 text-neutral-500">{en.reason}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-bold capitalize ${en.status === "active" ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"}`}>{en.status}</span>
                    {en.status === "active" && <Button data-testid="gov-release-btn" onClick={() => release(en.entry_id)} className="ml-3 h-7 rounded-lg bg-emerald-600 px-2.5 text-xs font-bold text-white hover:bg-emerald-700">Release</Button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

function Subject() {
  const [id, setId] = useState("");
  const [basis, setBasis] = useState("");
  const [result, setResult] = useState(null);
  const [state, setState] = useState("idle");
  const [msg, setMsg] = useState("");
  const lookup = async () => {
    if (!id.trim()) return; setMsg(""); setState("loading"); setResult(null);
    try { const r = await api.get(`/gov/subject/${encodeURIComponent(id.trim())}`); setResult(r.data); setState("ok"); }
    catch (err) { if (err.response?.status === 403) { setState("denied"); setMsg(formatApiError(err.response?.data?.detail)); } else { setState("idle"); setMsg(formatApiError(err.response?.data?.detail) || err.message); } }
  };
  const requestBG = async () => {
    if (!basis.trim()) { setMsg("A lawful basis is required."); return; }
    try { await api.post("/gov/break-glass/request", { subject_id: id.trim(), lawful_basis: basis.trim() }); setMsg("Break-glass request submitted for administrator approval. Retry the lookup once approved."); }
    catch (err) { setMsg(formatApiError(err.response?.data?.detail) || err.message); }
  };
  return (
    <Section title="Subject Lookup" subtitle="Deny-by-default. Sensitive records require an approved break-glass grant.">
      <div className={`${CARD} max-w-2xl`}>
        <Label className="text-neutral-600 text-xs">Subject / vehicle reference</Label>
        <div className="mt-1 flex gap-2">
          <Input data-testid="gov-subject-id" value={id} onChange={(e) => setId(e.target.value)} placeholder="e.g. AB-TRK-4471" className="h-11 rounded-xl" />
          <Button data-testid="gov-subject-lookup" onClick={lookup} className="rounded-xl bg-[#0A234A] font-bold text-white hover:bg-[#0d2c5e]">Look up</Button>
        </div>
        {state === "loading" && <Loading />}
        {state === "denied" && (
          <div data-testid="gov-subject-denied" className="mt-4 rounded-xl border border-[#FF5722]/30 bg-[#FF5722]/5 p-4">
            <p className="flex items-center gap-2 text-sm font-bold text-[#0A234A]"><Lock className="h-4 w-4 text-[#FF5722]" /> Restricted</p>
            <p className="mt-1 text-sm text-neutral-600">{msg}</p>
            <div className="mt-3 space-y-1"><Label className="text-neutral-600 text-xs">Lawful basis</Label>
              <Input data-testid="gov-bg-basis" value={basis} onChange={(e) => setBasis(e.target.value)} placeholder="Statutory authority / case reference" className="h-11 rounded-xl" /></div>
            <Button data-testid="gov-bg-request" onClick={requestBG} className="mt-3 rounded-xl bg-[#FF5722] font-bold text-white hover:bg-[#F4511E]">Submit break-glass request</Button>
          </div>
        )}
        {state === "ok" && result && (
          <div data-testid="gov-subject-record" className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm">
            <p className="flex items-center gap-2 font-bold text-[#0A234A]"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> {result.subject_id}</p>
            <p className="mt-1 text-neutral-600">Status: <b>{result.record?.status}</b> · HOS: <b>{result.record?.hos_state}</b> · Last seen: {result.record?.last_seen_corridor}</p>
            <p className="mt-1 text-xs text-neutral-500">{result.note}</p>
          </div>
        )}
        {state !== "denied" && msg && <p className="mt-3 text-sm text-neutral-600">{msg}</p>}
      </div>
    </Section>
  );
}

function BreakGlass() {
  const [d, setD] = useState(null);
  const load = useCallback(() => { api.get("/gov/break-glass/requests").then((r) => setD(r.data)).catch(() => setD({ requests: [], can_approve: false })); }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (id, kind) => { try { await api.post(`/gov/break-glass/${id}/${kind}`); load(); } catch {} };
  if (!d) return <Loading />;
  const statusPill = (s) => ({ pending: "bg-amber-50 text-amber-700", approved: "bg-emerald-50 text-emerald-700", denied: "bg-red-50 text-red-700" }[s] || "bg-neutral-100 text-neutral-600");
  return (
    <Section title="Break-glass Requests" subtitle={d.can_approve ? "Administrator review queue." : "Your elevation requests and their status."}>
      <div className="space-y-3">
        {d.requests.length === 0 && <div className={CARD}><p className="text-sm text-neutral-500">No requests yet.</p></div>}
        {d.requests.map((r) => (
          <div key={r.id} data-testid="gov-bg-row" className={`${CARD} flex flex-wrap items-center gap-3`}>
            <KeyRound className="h-4 w-4 text-[#FF5722]" />
            <div className="flex-1 min-w-[200px]">
              <p className="font-semibold text-[#0A234A]">Subject {r.subject_id} <span className="font-normal text-neutral-400">· {r.requester_email}</span></p>
              <p className="text-sm text-neutral-500">{r.lawful_basis}</p>
            </div>
            <span className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold capitalize ${statusPill(r.status)}`}>
              {r.status === "pending" ? <Clock className="h-3 w-3" /> : <BadgeCheck className="h-3 w-3" />}{r.status}
            </span>
            {d.can_approve && r.status === "pending" && (
              <div className="flex gap-2">
                <Button data-testid="gov-bg-approve" onClick={() => act(r.id, "approve")} className="h-9 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700">Approve</Button>
                <Button data-testid="gov-bg-deny" onClick={() => act(r.id, "deny")} variant="outline" className="h-9 rounded-lg">Deny</Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

function EldLogs() {
  const [d, setD] = useState(null);
  const load = useCallback(() => { api.get("/gov/eld/logs").then((r) => setD(r.data)).catch(() => setD({ logs: [], provenance: "unavailable", source: "not connected", summary: {} })); }, []);
  const [keys, setKeys] = useState([]);
  const [newKey, setNewKey] = useState("");
  const [keyMsg, setKeyMsg] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyKey = async () => { try { await navigator.clipboard.writeText(newKey); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {} };
  const loadKeys = useCallback(() => { api.get("/gov/eld/api-keys").then((r) => setKeys(r.data.keys || [])).catch(() => setKeys([])); }, []);
  const genKey = async () => {
    setKeyMsg(""); setNewKey("");
    try { const r = await api.post("/gov/eld/api-keys", { label: "Law enforcement" }); setNewKey(r.data.api_key); loadKeys(); }
    catch (err) { setKeyMsg(formatApiError(err.response?.data?.detail) || err.message); }
  };
  const doConfirm = async () => {
    if (!confirm) return;
    setBusy(true); setKeyMsg("");
    try {
      if (confirm.type === "roll") { const r = await api.post(`/gov/eld/api-keys/${confirm.id}/roll`, {}); setNewKey(r.data.api_key); }
      else { await api.delete(`/gov/eld/api-keys/${confirm.id}`); setNewKey(""); }
      loadKeys();
    } catch (err) { setKeyMsg(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); setConfirm(null); }
  };
  useEffect(() => { loadKeys(); }, [loadKeys]);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  if (!d) return <Loading />;
  const live = d.provenance === "live";
  const provCls = live ? "bg-emerald-50 text-emerald-700" : d.provenance === "sample" ? "bg-amber-50 text-amber-700" : "bg-neutral-100 text-neutral-500";
  return (
    <Section title="ELD Auto-Log (law-enforcement read API)" subtitle="Automatic hours-of-service / duty-status feed pulled from connected ELD/telematics. Read-only, jurisdiction-scoped, audited.">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span data-testid="gov-eld-provenance" className={`rounded-full px-3 py-1 text-xs font-bold ${provCls}`}>{live ? "LIVE FEED" : d.provenance === "sample" ? "SAMPLE — connect Samsara/Geotab to go live" : "NO FEED CONNECTED"}</span>
        <span className="text-xs text-neutral-500">source: <span className="font-semibold">{d.source}</span> · {(d.logs || []).length} units · refreshed {new Date(d.generated_at || Date.now()).toLocaleTimeString()}</span>
        <Button data-testid="gov-eld-refresh" onClick={load} className="ml-auto h-8 rounded-lg bg-[#0A234A] px-3 text-xs font-bold text-white hover:bg-[#0d2c5e]"><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh</Button>
      </div>
      <div className="mb-4 flex flex-wrap gap-2">
        {Object.entries(d.summary || {}).map(([k, v]) => <span key={k} className="rounded-lg bg-neutral-50 px-3 py-1.5 text-xs font-semibold text-[#0A234A]">{String(k).replace(/_/g, " ")}: {v}</span>)}
        {d.at_risk != null && <span className="rounded-lg bg-red-50 px-3 py-1.5 text-xs font-bold text-red-600">at risk: {d.at_risk}</span>}
      </div>
      <div className={CARD}>
        <div className="grid grid-cols-5 gap-2 border-b border-neutral-100 pb-2 text-xs font-bold uppercase tracking-wide text-neutral-400">
          <span>Unit</span><span>Duty status</span><span>Speed</span><span>Corridor</span><span>Position</span>
        </div>
        {(d.logs || []).length === 0 ? <p className="py-4 text-sm text-neutral-500">No ELD units reporting. Connect a provider under Integrations.</p> : (d.logs || []).map((l) => (
          <div key={l.vehicle_ref} data-testid="gov-eld-row" className="grid grid-cols-5 items-center gap-2 border-b border-neutral-50 py-2.5 text-sm">
            <span className="font-display font-bold text-[#0A234A]">{l.vehicle_ref}</span>
            <span className="capitalize text-neutral-600">{String(l.hos_state || "—").replace(/_/g, " ")}</span>
            <span className="text-neutral-600">{l.speed_kph ?? 0} km/h</span>
            <span className="text-neutral-500">{l.corridor || "—"}</span>
            <span className="font-mono text-xs text-neutral-400">{l.position ? `${l.position.lat?.toFixed(2)}, ${l.position.lng?.toFixed(2)}` : "—"}</span>
          </div>
        ))}
      </div>

      <div className={`${CARD} mt-6`}>
        <div className="mb-1 flex items-center gap-2"><KeyRound className="h-4 w-4 text-[#FF5722]" /><p className="font-display text-lg font-bold text-[#0A234A]">Law-enforcement API key</p></div>
        <p className="mb-4 text-sm text-neutral-500">One scoped key lets police systems pull this ELD feed at <span className="font-mono text-xs">GET /api/eld/logs</span> (header <span className="font-mono text-xs">x-api-key</span>) — no portal login. One active key per jurisdiction; roll or delete it; every action is audited.</p>
        {(() => { const active = keys.find((k) => !k.revoked); return (<>
          {!active && (
            <div className="mb-3 flex items-center gap-2">
              <Button data-testid="gov-eldkey-gen" onClick={genKey} className="h-9 rounded-sm bg-[#FF5722] px-4 font-bold text-white hover:bg-[#F4511E]">Generate key</Button>
              {keyMsg && <span className="text-sm text-red-600">{keyMsg}</span>}
            </div>
          )}
          {active ? (
            <div data-testid="gov-eldkey-row" className="flex flex-wrap items-center gap-2 rounded-sm border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm">
              <span className="font-mono text-xs font-bold text-[#0A234A]">{active.prefix}…</span>
              <span className="text-neutral-600">{active.label}</span>
              <span className="rounded-sm border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-bold text-emerald-700">active</span>
              <span className="ml-auto font-mono text-[11px] text-neutral-400">{active.last_used ? `used ${new Date(active.last_used).toLocaleDateString()}` : "unused"}</span>
              <Button data-testid="gov-eldkey-roll" onClick={() => { setNewKey(""); setConfirm({ type: "roll", id: active.key_id }); }} className="h-7 rounded-sm border border-slate-300 bg-white px-2.5 text-xs font-bold text-[#0A234A] hover:bg-slate-100"><RefreshCw className="mr-1 h-3 w-3" /> Roll</Button>
              <Button data-testid="gov-eldkey-delete" onClick={() => { setNewKey(""); setConfirm({ type: "delete", id: active.key_id }); }} className="h-7 rounded-sm bg-red-600 px-2.5 text-xs font-bold text-white hover:bg-red-700">Delete</Button>
            </div>
          ) : (!newKey && <p className="text-sm text-neutral-500">No active key. Generate one to grant police read access.</p>)}
          {keyMsg && active && <p className="mt-2 text-sm text-red-600">{keyMsg}</p>}
        </>); })()}
      </div>

      {newKey && (
        <div data-testid="gov-eldkey-reveal" className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-lg rounded-sm border border-slate-300 bg-white p-5 shadow-xl">
            <div className="mb-2 flex items-center gap-2"><KeyRound className="h-5 w-5 text-[#FF5722]" /><p className="font-display text-lg font-bold text-[#0A234A]">Copy your API key now</p></div>
            <p className="mb-3 text-sm text-neutral-600">This is the only time the key is shown. It's stored encrypted (one-way hash) and can never be displayed again — if it's lost, roll the key to issue a new one.</p>
            <div className="mb-4 flex items-stretch gap-2">
              <code data-testid="gov-eldkey-value" className="min-w-0 flex-1 select-all break-all rounded-sm border border-slate-300 bg-slate-50 p-3 font-mono text-xs text-[#0A234A]">{newKey}</code>
              <Button data-testid="gov-eldkey-copy" onClick={copyKey} className={`h-auto flex-shrink-0 rounded-sm px-4 text-sm font-bold text-white ${copied ? "bg-emerald-600" : "bg-[#0A234A] hover:bg-[#0d2c5e]"}`}>{copied ? <><CheckCircle2 className="mr-1.5 h-4 w-4" /> Copied</> : <><Download className="mr-1.5 h-4 w-4" /> Copy</>}</Button>
            </div>
            <div className="flex justify-end">
              <Button data-testid="gov-eldkey-reveal-done" onClick={() => { setNewKey(""); setCopied(false); }} className="h-9 rounded-sm border border-slate-300 bg-white px-4 text-sm font-semibold text-[#0A234A] hover:bg-slate-100">I've stored it — done</Button>
            </div>
          </div>
        </div>
      )}

      {confirm && (
        <div data-testid="gov-eldkey-confirm" className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/50 p-4" onClick={() => !busy && setConfirm(null)}>
          <div className="w-full max-w-md rounded-sm border border-slate-300 bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center gap-2">
              {confirm.type === "roll" ? <RefreshCw className="h-5 w-5 text-[#FF5722]" /> : <AlertTriangle className="h-5 w-5 text-red-600" />}
              <p className="font-display text-lg font-bold text-[#0A234A]">{confirm.type === "roll" ? "Roll this API key?" : "Delete this API key?"}</p>
            </div>
            <p className="mb-5 text-sm text-neutral-600">
              {confirm.type === "roll"
                ? "The current key stops working immediately and a new key value is issued (shown once). Any police system using the old key must be updated."
                : "The key is permanently deleted and any police system using it loses ELD access immediately. This cannot be undone."}
            </p>
            <div className="flex justify-end gap-2">
              <Button data-testid="gov-eldkey-confirm-cancel" onClick={() => setConfirm(null)} disabled={busy} className="h-9 rounded-sm border border-slate-300 bg-white px-4 text-sm font-semibold text-[#0A234A] hover:bg-slate-100">Cancel</Button>
              <Button data-testid="gov-eldkey-confirm-ok" onClick={doConfirm} disabled={busy} className={`h-9 rounded-sm px-4 text-sm font-bold text-white ${confirm.type === "roll" ? "bg-[#FF5722] hover:bg-[#F4511E]" : "bg-red-600 hover:bg-red-700"}`}>{busy ? "Working…" : confirm.type === "roll" ? "Roll key" : "Delete key"}</Button>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}

function Audit() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/gov/audit").then((r) => setD(r.data.entries)).catch(() => setD([])); }, []);
  if (!d) return <Loading />;
  return (
    <Section title="Audit Log" subtitle="Append-only. Every government access is recorded.">
      <div className={`${CARD} overflow-x-auto`}>
        <table className="w-full text-left text-sm">
          <thead><tr className="text-xs uppercase tracking-wide text-neutral-400"><th className="pb-2">Time</th><th className="pb-2">Actor</th><th className="pb-2">Action</th><th className="pb-2">Resource</th><th className="pb-2">Decision</th><th className="pb-2">Lawful basis</th></tr></thead>
          <tbody>
            {d.map((e) => (
              <tr key={e.id} data-testid="gov-audit-row" className="border-t border-neutral-100">
                <td className="py-2 text-neutral-500 whitespace-nowrap">{new Date(e.ts).toLocaleString()}</td>
                <td className="py-2 text-neutral-600">{e.actor_email}</td>
                <td className="py-2 font-semibold text-[#0A234A]">{e.action}</td>
                <td className="py-2 text-neutral-600">{e.resource}{e.resource_id && e.resource_id !== "-" ? ` (${e.resource_id})` : ""}</td>
                <td className="py-2"><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${e.decision === "allow" ? "bg-emerald-50 text-emerald-700" : e.decision === "deny" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>{e.decision}</span></td>
                <td className="py-2 text-neutral-500">{e.lawful_basis || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function Fleet() {
  const [status, setStatus] = useState(null);
  const [hos, setHos] = useState(null);
  const [safety, setSafety] = useState(null);
  const [updated, setUpdated] = useState(null);
  const load = useCallback(() => {
    api.get("/gov/telematics/status").then((r) => { setStatus(r.data); setUpdated(new Date()); }).catch(() => setStatus({ source: "not_connected", vehicles: [] }));
    api.get("/gov/telematics/hos").then((r) => setHos(r.data)).catch(() => setHos(null));
    api.get("/gov/telematics/safety").then((r) => setSafety(r.data)).catch(() => setSafety({ events: [] }));
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  if (!status) return <Loading />;
  const src = status.source;
  const live = src !== "not_connected";
  const hosState = (s) => ({ driving: "bg-emerald-50 text-emerald-700", on_duty: "bg-blue-50 text-blue-700", sleeper: "bg-violet-50 text-violet-700", off_duty: "bg-neutral-100 text-neutral-600" }[s] || "bg-neutral-100 text-neutral-600");
  return (
    <Section title="Live Fleet Telematics" subtitle="Vehicle positions, hours-of-service and safety events across the jurisdiction.">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span data-testid="gov-fleet-source" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${live ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
          <Radio className="h-3.5 w-3.5" /> {live ? `Live · ${src === "samsara" ? "Samsara" : "Geotab"}` : "Not connected — add a Samsara token or Geotab creds to go live"}
        </span>
        <span className="inline-flex items-center gap-1.5 text-xs text-neutral-400"><RefreshCw className="h-3 w-3" /> Auto-refresh 20s{updated ? ` · updated ${updated.toLocaleTimeString()}` : ""}</span>
      </div>

      {hos && (
        <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className={CARD}><p className="text-2xl font-bold text-[#0A234A] font-display">{hos.drivers}</p><p className="mt-1 text-xs font-semibold text-neutral-500">Drivers reporting</p></div>
          {Object.entries(hos.by_state || {}).slice(0, 3).map(([k, v]) => (
            <div key={k} className={CARD}><p className="text-2xl font-bold text-[#0A234A] font-display">{v}</p><p className="mt-1 text-xs font-semibold capitalize text-neutral-500">{k.replace(/_/g, " ")}</p></div>
          ))}
        </div>
      )}

      <div className={`${CARD} mb-6 overflow-x-auto`}>
        <p className="mb-4 font-display text-lg font-bold text-[#0A234A]">Vehicles</p>
        <table className="w-full text-left text-sm">
          <thead><tr className="text-xs uppercase tracking-wide text-neutral-400"><th className="pb-2">Unit</th><th className="pb-2">Corridor</th><th className="pb-2">Speed</th><th className="pb-2">HOS</th><th className="pb-2">Position</th><th className="pb-2">Last seen</th></tr></thead>
          <tbody>
            {status.vehicles.map((v) => (
              <tr key={v.id} data-testid="gov-fleet-row" className="border-t border-neutral-100">
                <td className="py-3 font-semibold text-[#0A234A]">{v.name}</td>
                <td className="py-3 text-neutral-500">{v.corridor || "—"}</td>
                <td className="py-3 text-neutral-600"><span className="inline-flex items-center gap-1"><Gauge className="h-3.5 w-3.5 text-neutral-400" />{v.speed_kph ?? 0} km/h</span></td>
                <td className="py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-bold capitalize ${hosState(v.hos_state)}`}>{String(v.hos_state).replace(/_/g, " ")}</span></td>
                <td className="py-3 text-neutral-500">{v.latitude != null ? `${v.latitude.toFixed(3)}, ${v.longitude.toFixed(3)}` : "—"}</td>
                <td className="py-3 text-neutral-400 whitespace-nowrap">{v.time ? new Date(v.time).toLocaleTimeString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {safety && (
        <div className={CARD}>
          <p className="mb-3 font-display text-lg font-bold text-[#0A234A]">Recent safety events</p>
          <div className="space-y-2">
            {(safety.events || []).map((e) => (
              <div key={e.id} data-testid="gov-fleet-safety-row" className="flex items-center gap-3 rounded-lg bg-neutral-50 px-3 py-2 text-sm">
                <span className={`rounded-full px-2 py-0.5 text-xs font-bold capitalize ${sevColor(e.severity)}`}>{e.severity}</span>
                <span className="font-semibold text-[#0A234A]">{e.vehicle}</span>
                <span className="text-neutral-500">{e.behavior}</span>
                <span className="ml-auto text-xs text-neutral-400">{e.corridor}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
}

function Provisioning() {
  const [d, setD] = useState(null);
  const load = useCallback(() => { api.get("/gov/provisioning").then((r) => setD(r.data)).catch(() => setD({ users: [] })); }, []);
  useEffect(() => { load(); }, [load]);
  const verify = async (id) => { try { await api.post(`/gov/provisioning/${id}/verify`); load(); } catch {} };
  if (!d) return <Loading />;
  return (
    <Section title="Provisioning" subtitle="Government account administration and verification.">
      <div className="space-y-3">
        {d.users.map((u) => (
          <div key={u.user_id} data-testid="gov-prov-row" className={`${CARD} flex flex-wrap items-center gap-3`}>
            <ShieldCheck className="h-4 w-4 text-[#FF5722]" />
            <div className="flex-1 min-w-[200px]">
              <p className="font-semibold text-[#0A234A]">{u.name} <span className="font-normal text-neutral-400">· {u.email}</span></p>
              <p className="text-xs text-neutral-500 capitalize">{u.role.replace(/_/g, " ")} · {u.jurisdiction_id || "—"}</p>
            </div>
            <span className={`rounded-full px-2.5 py-1 text-xs font-bold capitalize ${u.gov_status === "verified" ? "bg-emerald-50 text-emerald-700" : u.gov_status === "pending_review" ? "bg-amber-50 text-amber-700" : "bg-neutral-100 text-neutral-600"}`}>{u.gov_status || (u.role === "admin" ? "admin" : "—")}</span>
            {u.role === "gov_agency" && u.gov_status !== "verified" && (
              <Button data-testid="gov-prov-verify" onClick={() => verify(u.user_id)} className="h-9 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700">Verify</Button>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

function FleetOwners() {
  const [reg, setReg] = useState(null);
  const [ua, setUa] = useState(null);
  useEffect(() => {
    api.get("/gov/fleet-owners").then((r) => setReg(r.data)).catch(() => setReg({ fleets: [], facilities: [], telematics_active: 0 }));
    api.get("/gov/usage-analytics").then((r) => setUa(r.data)).catch(() => setUa({ dau: [], summary: {} }));
  }, []);
  if (!reg || !ua) return <Loading />;
  const s = ua.summary || {};
  const dau = ua.dau || [];
  const maxDau = Math.max(1, ...dau.map((d) => d.users));
  const hasActivity = dau.some((d) => d.users > 0);
  const kpis = [
    { l: "Active today", n: s.dau_today ?? 0 },
    { l: "Weekly active", n: s.wau ?? 0 },
    { l: "Monthly active", n: s.mau ?? 0 },
    { l: "Fleet owners", n: (reg.fleets || []).length },
    { l: "FleetPod sites", n: (reg.facilities || []).length },
    { l: "Weekly churn", n: `${s.weekly_churn_pct ?? 0}%` },
  ];
  return (
    <Section title="Fleet Owners & Platform Activity" subtitle="Live registry of fleet owners and how they use FleetPods. Usage metrics accrue from real activity — no sample data.">
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-sm border border-slate-200 bg-slate-200 sm:grid-cols-3 lg:grid-cols-6">
        {kpis.map((x) => (
          <div key={x.l} data-testid={`fo-kpi-${x.l.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="bg-white p-3">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-wider text-slate-400">{x.l}</p>
            <p className="mt-1.5 font-mono text-3xl font-medium tracking-tight text-slate-900">{x.n}</p>
          </div>
        ))}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <div className={`${CARD} lg:col-span-2`}>
          <div className="mb-3 flex items-center justify-between">
            <p className="flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-700"><TrendingUp className="h-3.5 w-3.5 text-[#FF5722]" /> Daily active users · 14 days</p>
            {!hasActivity && <span className="rounded-sm border border-amber-300 bg-amber-50 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-amber-700">Building live data</span>}
          </div>
          <div className="flex h-44 items-end gap-1.5" data-testid="fo-dau-chart">
            {dau.map((d) => (
              <div key={d.day} className="flex flex-1 flex-col items-center gap-1" title={`${d.day}: ${d.users} active · ${d.new_users} new`}>
                <div className="flex w-full flex-1 items-end">
                  <div className="w-full rounded-t-sm bg-[#FF5722] transition-[height] duration-500" style={{ height: `${(d.users / maxDau) * 100}%`, minHeight: d.users > 0 ? 3 : 0 }} />
                </div>
                <span className="font-mono text-[8px] text-slate-400">{d.day.slice(5)}</span>
              </div>
            ))}
          </div>
          {!hasActivity && <p className="mt-3 text-center font-mono text-[11px] text-slate-400">Tracking started today — daily active users and churn will populate as fleets use the console.</p>}
        </div>

        <div className={CARD}>
          <p className="mb-3 flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-700"><Building2 className="h-3.5 w-3.5 text-[#FF5722]" /> FleetPod sites</p>
          <div className="max-h-[176px] space-y-2 overflow-y-auto">
            {(reg.facilities || []).length === 0 && <p className="py-6 text-center font-mono text-[11px] text-slate-400">No FleetPod sites registered yet.</p>}
            {(reg.facilities || []).map((f) => (
              <div key={f.facility_id} data-testid="fo-facility-row" className="flex items-center justify-between gap-2 rounded-sm border border-slate-100 bg-slate-50 px-3 py-2">
                <div className="min-w-0"><p className="truncate text-sm font-semibold text-slate-900">{f.name}</p><p className="truncate font-mono text-[10px] text-slate-400">{f.location || "—"}{f.pods ? ` · ${f.pods} pods` : ""}</p></div>
                {f.occupancy_pct != null && <span className="flex-shrink-0 font-mono text-[11px] font-semibold tabular-nums text-slate-600">{f.occupancy_pct}%</span>}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={`${CARD} mt-4`}>
        <div className="mb-3 flex items-center justify-between">
          <p className="flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-700"><Truck className="h-3.5 w-3.5 text-[#FF5722]" /> Fleet owner registry</p>
          <span className="rounded-sm bg-slate-100 px-1.5 font-mono text-[10px] tabular-nums text-slate-600">{(reg.fleets || []).length}</span>
        </div>
        {(reg.fleets || []).length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-sm border border-dashed border-slate-200 py-10 text-center">
            <Truck className="h-6 w-6 text-slate-300" />
            <p className="text-sm font-semibold text-slate-500">No fleet owners onboarded yet</p>
            <p className="max-w-sm text-xs text-slate-400">This registry fills automatically as fleet owners join FleetPods in this jurisdiction. No sample data is shown.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead><tr className="border-b border-slate-200 font-mono text-[10px] uppercase tracking-wider text-slate-400">
                <th className="py-2 pr-3">Fleet owner</th><th className="py-2 pr-3">Onboarded</th><th className="py-2 pr-3">Fleet ID</th>
              </tr></thead>
              <tbody>
                {reg.fleets.map((t) => (
                  <tr key={t.tenant_id} data-testid="fo-fleet-row" className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-2.5 pr-3 font-semibold text-slate-900">{t.name}</td>
                    <td className="py-2.5 pr-3 text-slate-500">{t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}</td>
                    <td className="py-2.5 pr-3 font-mono text-[11px] text-slate-400">{t.tenant_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Section>
  );
}

const SECTIONS = { overview: Overview, map: CommandMap, owners: FleetOwners, fleet: Fleet, eld: EldLogs, corridors: Corridors, facilities: Facilities, compliance: Compliance, exceptions: Exceptions, zones: Zones, subject: Subject, breakglass: BreakGlass, provisioning: Provisioning, integrations: GovIntegrations, reports: Reports, audit: Audit };

function GovIntegrations() {
  return <Section title="Integrations" subtitle="Connect telematics (Samsara/Geotab) and road-event (511 Alberta) APIs. Keys apply platform-wide."><IntegrationsPanel /></Section>;
}

export default function GovConsole() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("overview");
  const [jurName, setJurName] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [critical, setCritical] = useState(null);
  const [dismissed, setDismissed] = useState(() => new Set());
  const isAdmin = user?.role === "admin";
  useEffect(() => { api.get("/gov/me").then((r) => setJurName(r.data?.jurisdiction?.name || null)).catch(() => setJurName(null)); }, []);
  useEffect(() => {
    const loadCritical = () => api.get("/gov/announcements")
      .then((r) => { const list = r.data.announcements || []; setCritical(list.find((a) => a.severity === "critical") || null); })
      .catch(() => setCritical(null));
    loadCritical();
    const t = setInterval(loadCritical, 60000);
    return () => clearInterval(t);
  }, []);
  const navItems = [...(isAdmin ? [...NAV, { id: "provisioning", label: "Provisioning", icon: ShieldCheck, mode: "live" }, { id: "integrations", label: "Integrations", icon: KeyRound, mode: "live" }] : NAV), { id: "security", label: "Security", icon: Lock, mode: "live" }];
  const mfaBlocked = user?.mfa_required && !user?.session_mfa;
  const Active = mfaBlocked || section === "security" ? SecurityCenter : SECTIONS[section] || Overview;
  const signOut = async () => { navigate("/", { replace: true }); await logout(); };

  return (
    <div data-testid="gov-console" className="gov-shell min-h-[100dvh] bg-slate-50 text-slate-900">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white">
        <div className="flex h-14 items-center justify-between px-5">
          <div className="flex items-center gap-4">
            <img src="/fleetpods_logo.png" alt="FleetPods" className="h-6 w-auto" />
          </div>
        </div>
      </header>

      {critical && !dismissed.has(critical.id) && (
        <div data-testid="gov-critical-banner" className="sticky top-14 z-30 flex items-center gap-3 border-b border-red-700 bg-red-600 px-5 py-2 text-white shadow-md">
          <span className="flex items-center gap-1.5 rounded-sm bg-white/20 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest">
            <Megaphone className="h-3.5 w-3.5 animate-pulse" /> Critical
          </span>
          <p className="min-w-0 flex-1 truncate text-sm font-semibold"><span className="font-bold">{critical.title}</span> — {critical.body}</p>
          <span className="hidden flex-shrink-0 font-mono text-[10px] text-white/70 sm:inline">{critical.author_name || critical.author_email}</span>
          <button data-testid="gov-critical-dismiss" onClick={() => setDismissed((s) => new Set(s).add(critical.id))} title="Dismiss" className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-sm text-white/80 transition-colors hover:bg-white/20 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="flex">
        <aside className={`sticky top-[57px] hidden h-[calc(100dvh-57px)] flex-shrink-0 flex-col border-r border-slate-200 bg-white transition-[width] duration-200 lg:flex ${collapsed ? "w-16" : "w-60"}`}>
          <div className={`flex h-10 flex-shrink-0 items-center border-b border-slate-100 ${collapsed ? "justify-center" : "justify-between px-4"}`}>
            {!collapsed && <span className="font-mono text-[10px] font-semibold uppercase tracking-widest text-slate-400">Console</span>}
            <button data-testid="gov-sidebar-collapse" onClick={() => setCollapsed((v) => !v)} title={collapsed ? "Expand" : "Collapse"}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700">
              {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto py-2">
            {navItems.map((n) => {
              const active = section === n.id;
              return (
                <button key={n.id} data-testid={`gov-nav-${n.id}`} onClick={() => setSection(n.id)} title={collapsed ? `${n.label} (${n.mode === "sandbox" ? "Sandbox" : "Live"})` : undefined}
                  className={`flex w-full items-center gap-3 border-l-2 py-2 text-sm font-medium transition-colors ${collapsed ? "justify-center px-0" : "px-4"} ${active ? "border-[#FF5722] bg-[#FF5722]/5 text-slate-900" : "border-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-800"}`}>
                  <span className="relative flex-shrink-0">
                    <n.icon className={`h-4 w-4 ${active ? "text-[#FF5722]" : "text-slate-400"}`} />
                    {collapsed && <span className={`absolute -right-1 -top-1 h-1.5 w-1.5 rounded-full ${n.mode === "sandbox" ? "bg-amber-500" : "bg-emerald-500"}`} />}
                  </span>
                  {!collapsed && <span className="flex-1 text-left">{n.label}</span>}
                  {!collapsed && <DataTag mode={n.mode} />}
                </button>
              );
            })}
          </nav>
          <div className="flex-shrink-0 border-t border-slate-200 p-3">
            {collapsed ? (
              <button data-testid="gov-signout" onClick={signOut} title={`Sign out — ${user?.name || ""}`}
                className="flex h-9 w-full items-center justify-center rounded-sm border border-slate-300 text-slate-600 transition-colors hover:bg-slate-100 hover:text-red-600">
                <LogOut className="h-4 w-4" />
              </button>
            ) : (
              <>
                <div className="mb-2 min-w-0 px-1">
                  <p className="truncate text-sm font-semibold text-slate-900" data-testid="gov-user-name">{user?.name}</p>
                  <p className="truncate font-mono text-[11px] text-slate-400">{user?.email}</p>
                </div>
                <button data-testid="gov-signout" onClick={signOut}
                  className="flex w-full items-center justify-center gap-2 rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-600">
                  <LogOut className="h-4 w-4" /> Sign out
                </button>
              </>
            )}
          </div>
        </aside>

        <main className="min-w-0 flex-1 px-5 py-6 lg:px-8">
          <div className="mb-5 flex items-center gap-1.5 overflow-x-auto lg:hidden">
            {navItems.map((n) => (
              <button key={n.id} onClick={() => setSection(n.id)} className={`whitespace-nowrap rounded-sm border px-3 py-1.5 text-xs font-semibold ${section === n.id ? "border-[#FF5722] bg-[#FF5722]/5 text-[#FF5722]" : "border-slate-200 bg-white text-slate-600"}`}>{n.label}</button>
            ))}
            <button data-testid="gov-signout-mobile" onClick={signOut} className="ml-auto flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700"><LogOut className="h-3.5 w-3.5" /> Sign out</button>
          </div>
          {user?.privileged && !user?.mfa_enabled && !mfaBlocked && section !== "security" && (
            <div data-testid="gov-mfa-nudge" className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-sm border border-amber-300 bg-amber-50 px-4 py-3">
              <p className="text-sm font-semibold text-amber-800">Protect this government account with an authenticator app. Privileged sessions time out after 15 minutes idle.</p>
              <button data-testid="gov-mfa-nudge-open" onClick={() => setSection("security")} className="rounded-sm bg-[#0A234A] px-3 py-1.5 text-xs font-bold text-white">Set up now</button>
            </div>
          )}
          <Active />
        </main>
      </div>
    </div>
  );
}
